import { Box } from "@mui/material";
import { useInterfaceDesign } from "../design/InterfaceDesign";
import { behaviorPresentation } from "../design/behaviorPresentation";
import { designTokens } from "../design/tokens";
import { useEditorActiveRef } from "../design/EditorActivity";
import { isTimerAction } from "../../../util/src/behavior-timers/protocol";
import { dateDifferencePorts } from "../../../util/src/blueprint/date-operators.js";
import { areBehaviorPortTypesCompatible } from "../../../util/src/blueprint/port-compatibility.js";
import { synchronizeBehaviorObjectPaths } from "../../../util/src/blueprint/object-path-typing.js";
import {
  timerReadPorts,
  timerActionPorts,
} from "../../../util/src/behavior-timers/nodes";
import { ObjectEndNode } from "./controls/ObjectEndNode";
import {
  updateSceneObjectDefinitionPorts,
  objectBaseSource,
  sceneObjectExtensions,
  sceneObjectTypeName,
} from "../../../util/src/layout/scene-object-types";
import { SceneObjectTypesProvider } from "./SceneObjectTypesContext";
import {
  createSceneObjectConstructor,
  sceneObjectConstructorPorts,
  sceneObjectTypePaths,
  SCENE_OBJECT_BASE_SOURCES,
} from "../../../util/src/layout/scene-object-types";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  DragEvent,
  MouseEvent,
  MouseEventHandler,
  WheelEvent as ReactWheelEvent,
} from "react";
import {
  type AnimatableProperty,
  type ActionSetKey,
  type DatePartTextFormat,
  type LayoutListTargetOption,
  type ListValue,
  type LocationValue,
  type LayoutListTargetValue,
  type NodePortValue,
  type Operator,
  type Port,
  type TransformValue,
  type VariableValueType,
} from "../../../util/src/blueprint/types.js";
import {
  ACTION_SET_KEY_VALUE_TYPE,
  WHITE_SPACE_VALUES,
  ANIMATION_DIRECTION_VALUES,
  ANIMATION_EASING_VALUES,
  ANIMATION_FILL_MODE_VALUES,
  getValueTypeColor,
  isColorString,
  isDateString,
  isKnownFontFamilyValue,
  normalizeFontWeightValue,
  normalizeTextAlignValue,
  normalizeTextTransformValue,
  isFilterString,
  isGradientString,
  isBoxShadowString,
  isTextShadowString,
  type BehaviorConnection,
  type BehaviorNode,
  type BehaviorPortPosition,
  type CustomSceneEventNodeTemplate,
} from "../../../util/src/blueprint/index.js";
import {
  DATA_SOURCE_PATHS,
  getFlattenedPathOptions,
  getPathEntryValueType,
  getPathSchemaAtPath,
  getSchemaValueTypeForFieldPath,
  getScalarSourceValueType,
  sourceHasPaths,
} from "../../../util/src/layout/data-source-schema.js";
import type { PathEntry } from "../../../util/src/layout/data-source-schema.js";
import {
  BehaviorPathSchemaProvider,
  resolveBehaviorPathSchema,
  type BehaviorPathSchemaMap,
} from "./BehaviorPathSchemaContext.js";
import type { BehaviorCanvasScope } from "./BehaviorCanvasScope.js";
import { getMockLayoutData } from "../../../util/src/layout/mock.js";
import { resolvePath } from "../../../util/src/layout/resolution.js";
import { allocateUniqueCatalogName } from "../../../util/src/layout/scene-catalog-names.js";
import { OperatorNode } from "./operators/OperatorNode.js";
import { EventNode } from "./events/EventNode.js";
import { ControlIfElseNode } from "./controls/ControlIfElseNode.js";
import { ControlWaitNode } from "./controls/ControlWaitNode";
import { ControlMergeFlowsNode } from "./controls/ControlMergeFlowsNode";
import { EventOutputNode } from "./controls/EventOutputNode";
import { FunctionBeginNode } from "./controls/FunctionBeginNode";
import { FunctionEndNode } from "./controls/FunctionEndNode";
import { VariableBeginNode } from "./controls/VariableBeginNode";
import { VariableEndNode } from "./controls/VariableEndNode";
import { ControlRepeatNode } from "./controls/ControlRepeatNode";
import { ActionAnimationNode } from "./actions/ActionAnimationNode.js";
import { ActionSetNode } from "./actions/ActionSetNode.js";
import { VariableNode } from "./variables/VariableNode.js";
import { VariableRefNode as VariableRefNodeComponent } from "./variables/VariableRefNode.js";
import { FunctionCallNode } from "./actions/FunctionCallNode.js";
import { SetVariableNode } from "./actions/SetVariableNode.js";
import { Scissors } from "../svg/Scissors.js";
import {
  BEHAVIOR_NODE_DRAG_MIME as BEHAVIOR_NODE_DRAG_MIME,
  type BehaviorDraggedNodeTemplate,
  BehaviorNodeList,
} from "./BehaviorNodeList.js";
import { SceneCatalogNodeList } from "./SceneCatalogNodeList.js";
import dico from "../../../util/src/language/dico.js";
import { BehaviorContextMenu } from "./BehaviorContextMenu.js";
import { getBehaviorCanvasViewportSize } from "./behaviorCanvasViewport.js";
import { LayoutNode } from "../../../util/src/layout/types.js";
import type { DataSourceId } from "../../../util/src/layout/types.js";
import type { BorderConfig } from "../components/BorderPicker.js";
import type { BorderRadiusConfig } from "../components/BorderRadiusPicker.js";
import type { SpacingConfig } from "../components/SpacingPicker.js";
import { BAR_LOADER_DISKS_BY_UNIT } from "../display-editor/components/barLoaderConfig.js";
import { ActionLayoutListNode } from "./actions/ActionLayoutListNode.js";
import { FilterSvgPickerContext } from "./FilterSvgPickerContext.js";
import { BehaviorFontContext } from "./BehaviorFontContext.js";
import type { BehaviorClipboardPayload } from "../display-editor/editor/editorClipboard.js";
import {
  getInMemoryClipboardPayload,
  isClipboardPayloadForScope,
  readClipboardPayloadFromSystem,
  setInMemoryClipboardPayload,
  writeClipboardPayloadToSystem,
} from "../display-editor/editor/editorClipboard.js";
import {
  getBehaviorTemplateCatalog,
  type BehaviorTemplateCatalogItem,
} from "./BehaviorTemplateCatalog.js";
import { sceneAnimationGraphHasTerminalKind } from "../../../util/src/blueprint/runtime/scene-animations.js";
import {
  triggerGraphHasEventOutput,
  isSelfReferencingCustomSceneEventNode,
} from "../../../util/src/blueprint/trigger-graph.js";
import {
  functionGraphHasFunctionBegin,
  functionGraphHasFunctionEnd,
  buildFunctionCallPorts,
  syncFunctionGraphTerminalPorts,
  syncCallNodePortsInGraph,
} from "../../../util/src/blueprint/function-graph.js";
import {
  addAnimKeyframePortAcrossGraph,
  buildAnimKeyframePorts,
  getAnimKeyframePropertyIds,
  keyframeHasEasingPort,
  removeAnimKeyframePortAcrossGraph,
  type KeyframeAddablePortId,
} from "../../../util/src/blueprint/anim-keyframe-ports.js";
import {
  variableGraphHasVariableBegin,
  variableGraphHasVariableEnd,
  buildVariableRefPorts,
  buildSetVariablePorts,
  syncVariableGraphTerminalPorts,
  syncVariableRefNodePortsInGraph,
  syncSetVariableNodePortsInGraph,
  analyzeSelectionForVariableExtraction,
  extractBehaviorSelectionToSceneVariable,
} from "../../../util/src/blueprint/variable-graph.js";
import {
  analyzeSelectionForFunctionExtraction,
  extractBehaviorSelectionToSceneFunction,
} from "../../../util/src/blueprint/function-graph.js";
import {
  getSceneCatalogTemplateCatalogItems,
  resolveSceneCatalogDetailRoute,
} from "./SceneCatalogTemplateCatalog.js";
import type { SceneBehaviorRoute } from "./SceneBehaviorNavigator.js";
import { applyAnimPlayDefaultsToPorts } from "./animPlayPortDefaults.js";
import {
  PortConnectionPicker,
  type PortConnectionPickerItem,
} from "./PortConnectionPicker.js";

const LAYOUT_LIST_ROW_ITEM_OBJECT_SOURCE = "layoutListRowItem";
const LAYOUT_LIST_COMPARATOR_OBJECT_SOURCE = "layoutListComparator";

function resolveLayoutListBindingSource(
  layoutNode: LayoutNode,
): DataSourceId | undefined {
  if (
    layoutNode.kind !== "component" ||
    layoutNode.component !== "layoutList"
  ) {
    return undefined;
  }
  const source = (layoutNode.dataBinding?.path.split(".")[0] ??
    "") as DataSourceId;
  if (!source || !sourceHasPaths(source)) {
    return undefined;
  }
  return source;
}

/**
 * Maps the owning layout node's geometry and style to KeyFrame port defaults
 * when a new `animKeyframe` node is dropped on the behavior canvas.
 */
function getAnimKeyframeDefaultsFromLayoutNode(
  layoutNode: LayoutNode,
): Record<AnimatableProperty, number> {
  const transform =
    typeof layoutNode.style?.transform === "string"
      ? layoutNode.style.transform
      : "";
  const parseSkew = (axis: "X" | "Y"): number => {
    const match = transform.match(
      new RegExp(`skew${axis}\\(\\s*(-?\\d+(?:\\.\\d+)?)deg\\s*\\)`, "i"),
    );
    const value = Number(match?.[1] ?? 0);
    return Number.isFinite(value) ? Math.round(value) : 0;
  };
  const o = layoutNode.style?.opacity;
  const opacity =
    typeof o === "number" && Number.isFinite(o)
      ? Math.max(0, Math.min(1, o))
      : 1;
  return {
    opacity,
    x: 0,
    y: 0,
    scale: 1,
    scaleX: 1,
    scaleY: 1,
    rotation: Math.round(layoutNode.rotation ?? 0),
    skewX: parseSkew("X"),
    skewY: parseSkew("Y"),
    width: Math.round(layoutNode.width),
    height: Math.round(layoutNode.height),
  };
}

/** Snapshot used to apply blueprint nodes + connections in one editor update (avoids stale merges). */
export type BehaviorGraphSnapshot = {
  nodes: BehaviorNode[];
  connections: BehaviorConnection[];
};

export interface BehaviorCanvasProps {
  node: LayoutNode;
  nodes: BehaviorNode[];
  connections: BehaviorConnection[];
  allowRowIndexVariable?: boolean;
  layoutListBindingSource?: DataSourceId;
  scope?: BehaviorCanvasScope;
  sceneAnimationNames?: string[];
  sceneAnimationsByName?: Record<
    string,
    import("../../../util/src/blueprint/runtime/types.js").BehaviorAnimationPayload
  >;
  onNodesChange?: (nodes: BehaviorNode[]) => void;
  onConnectionsChange?: (connections: BehaviorConnection[]) => void;
  /**
   * When set, {@link BehaviorCanvas} uses this for updates that change both nodes and connections
   * at once (e.g. stripping wires when a port value type changes). Required for parents whose
   * `onNodesChange` / `onConnectionsChange` each call `updateNode` with a stale document copy.
   */
  onBehaviorGraphCommit?: (graph: BehaviorGraphSnapshot) => void;
  viewport?: BehaviorViewportState;
  onViewportChange?: (viewport: BehaviorViewportState) => void;
  onPickFilterSvg?: () => Promise<string | null>;
  /** Opens the font file picker and returns the uploaded family name. */
  onPickFont?: () => Promise<string | null>;
  /** Custom uploaded font families shown in fontFamily port dropdowns. */
  customFontFamilies?: string[];
  /** Overlay of `get` field paths for discipline-specific object sources. */
  customObjectPathSchema?: BehaviorPathSchemaMap | null;
  readOnly?: boolean;
  /** Layout list nodes available for list-target dropdowns. */
  layoutListTargetOptions?: LayoutListTargetOption[];
  /** Scene catalog for the right-hand palette (node behaviors only). */
  sceneCatalog?: import("../display-editor/layout-types").SceneBehaviorCatalog;
  /** Whether to show the right-hand SceneCatalogNodeList panel. */
  showSceneCatalogPanel?: boolean;
  /** Limits which sections appear in the right-hand scene catalog panel. */
  catalogPanelSections?: import("./SceneCatalogTemplateCatalog.js").SceneCatalogReferenceSection[];
  /** Opens a scene catalog entry in the scene behavior navigator. */
  onOpenSceneCatalogEntry?: (route: SceneBehaviorRoute) => void;
  /** When editing a custom event trigger graph, its catalog entry ID. */
  editingCustomEventId?: string;
  /** When editing a scene function graph, its catalog entry ID. */
  editingFunctionId?: string;
  /** Persists function signature (inputs/outputs) and graph terminals together. */
  onFunctionPortsCommit?: (
    entryId: string,
    patch: {
      inputs: import("../display-editor/layout-types").SceneFunctionPort[];
      outputs: import("../display-editor/layout-types").SceneFunctionPort[];
      nodes: BehaviorNode[];
      connections: BehaviorConnection[];
    },
  ) => void;
  /** When editing a scene variable graph, its catalog entry ID. */
  editingVariableId?: string;
  editingObjectTypeId?: string;
  /** Persists variable signature (inputs/output type) and graph terminals together. */
  onVariablePortsCommit?: (
    entryId: string,
    patch: {
      inputs: import("../display-editor/layout-types").SceneVariableInputPort[];
      outputValueType: import("../../../util/src/blueprint/types.js").VariableValueType;
      outputObjectTypeId?: string;
      nodes: BehaviorNode[];
      connections: BehaviorConnection[];
    },
  ) => void;
  /** Persists a new catalog variable extracted from the current node-behavior selection. */
  onCreateVariableFromSelection?: (result: {
    catalog: import("../display-editor/layout-types").SceneBehaviorCatalog;
    parentGraph: BehaviorGraphSnapshot;
  }) => void;
  /** Persists a new catalog function extracted from the current node-behavior selection. */
  onCreateFunctionFromSelection?: (result: {
    catalog: import("../display-editor/layout-types").SceneBehaviorCatalog;
    parentGraph: BehaviorGraphSnapshot;
  }) => void;
}

export interface BehaviorCanvasClipboardHandle {
  hasSelection: () => boolean;
  copySelectionToPayload: () => BehaviorClipboardPayload | null;
  cutSelectionToPayload: () => BehaviorClipboardPayload | null;
  pasteBehaviorPayload: (payload: BehaviorClipboardPayload) => string[];
}

function applyDatePartFormatToBehaviorNodes(
  nodeList: BehaviorNode[],
  nodeId: string,
  format: DatePartTextFormat | undefined,
): { mapped: BehaviorNode[]; stripOutgoingFromOutput: boolean } {
  let stripOutgoingFromOutput = false;
  const mapped = nodeList.map((node) => {
    if (node.id !== nodeId || node.type !== "operator") {
      return node;
    }
    if (node.mode !== "unary") {
      return node;
    }
    if (node.operator !== "getDay" && node.operator !== "getMonth") {
      return node;
    }

    const previousOutput = node.ports.find((p) => p.id === "output");
    const previousValueType = previousOutput?.valueType ?? "number";

    const outputValueType: "number" | "string" =
      format === undefined || format === "numeric" ? "number" : "string";

    if (previousValueType !== outputValueType) {
      stripOutgoingFromOutput = true;
    }

    const next = { ...node } as BehaviorNode & {
      datePartTextFormat?: DatePartTextFormat;
    };
    if (format !== undefined) {
      next.datePartTextFormat = format;
    } else {
      delete next.datePartTextFormat;
    }
    next.ports = node.ports.map((port) =>
      port.id === "output"
        ? { ...port, valueType: outputValueType, value: undefined }
        : port,
    );
    return next;
  });
  return { mapped, stripOutgoingFromOutput };
}

export interface BehaviorViewportState {
  offset: { x: number; y: number };
  zoom: number;
}

export const BehaviorCanvas = forwardRef<
  BehaviorCanvasClipboardHandle,
  BehaviorCanvasProps
>(
  (
    {
      node,
      nodes,
      connections,
      allowRowIndexVariable = false,
      layoutListBindingSource: layoutListBindingSourceProp,
      scope = "node",
      sceneAnimationNames = [],
      sceneAnimationsByName = {},
      onNodesChange,
      onConnectionsChange,
      onBehaviorGraphCommit,
      viewport,
      onViewportChange,
      onPickFilterSvg,
      onPickFont,
      customFontFamilies,
      customObjectPathSchema = null,
      readOnly = false,
      layoutListTargetOptions = [],
      sceneCatalog,
      showSceneCatalogPanel = false,
      catalogPanelSections,
      onOpenSceneCatalogEntry,
      editingCustomEventId,
      editingFunctionId,
      onFunctionPortsCommit,
      editingVariableId,
      editingObjectTypeId,
      onVariablePortsCommit,
      onCreateVariableFromSelection,
      onCreateFunctionFromSelection,
    },
    ref,
  ) => {
    const { mode: interfaceMode, colorMode } = useInterfaceDesign();
    const modern = interfaceMode === "modern";
    const palette = designTokens[colorMode];
    const behaviorDico = dico().behavior;
    const editorActiveRef = useEditorActiveRef();
    const effectiveObjectPathSchema = useMemo(
      () => ({
        ...customObjectPathSchema,
        ...Object.fromEntries(
          (sceneCatalog?.objectTypes ?? []).flatMap((entry) =>
            sceneObjectExtensions(entry).map((extension) => [
              objectBaseSource(entry.id, extension.portId),
              sceneObjectTypePaths(sceneCatalog, extension.typeId),
            ]),
          ),
        ),
        "@objectEntries": [
          { path: "key", valueType: "string" as const },
          { path: "value", valueType: "object" as const },
        ],
        ...Object.fromEntries(
          SCENE_OBJECT_BASE_SOURCES.map((source) => [
            "@object:source:" + source,
            sceneObjectTypePaths(sceneCatalog, "source:" + source),
          ]),
        ),
        ...Object.fromEntries(
          (sceneCatalog?.objectTypes ?? []).map((t) => [
            "@object:" + t.id,
            sceneObjectTypePaths(sceneCatalog, t.id),
          ]),
        ),
      }),
      [customObjectPathSchema, sceneCatalog],
    );
    const getPathSchema = useCallback(
      (source: string | undefined): PathEntry[] | null =>
        resolveBehaviorPathSchema(
          source,
          source?.startsWith("@object:source:")
            ? {
                ...effectiveObjectPathSchema,
                [source]: sceneObjectTypePaths(sceneCatalog, source.slice(8)),
              }
            : effectiveObjectPathSchema,
        ),
      [effectiveObjectPathSchema, sceneCatalog],
    );
    const canvasDico = behaviorDico.canvas;
    const [dragConnection, setDragConnection] = useState<{
      nodeId: string;
      port: Port;
      to: BehaviorPortPosition;
    } | null>(null);
    const [dragNode, setDragNode] = useState<{
      nodeIds: string[];
      startPointer: BehaviorPortPosition;
      initialPositions: Record<string, { x: number; y: number }>;
    } | null>(null);
    const [internalNodes, setInternalNodes] = useState<BehaviorNode[]>(nodes);
    const [isDeleteModifierPressed, setIsDeleteModifierPressed] =
      useState(false);
    const [internalViewportOffset, setInternalViewportOffset] = useState({
      x: 0,
      y: 0,
    });
    const [internalViewportZoom, setInternalViewportZoom] = useState(1);
    const [dragViewport, setDragViewport] = useState<{
      startClientX: number;
      startClientY: number;
      startOffsetX: number;
      startOffsetY: number;
    } | null>(null);
    const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
    const [nodeMenu, setNodeMenu] = useState<{
      mouseX: number;
      mouseY: number;
      selectionNodeIds: string[];
      canCreateVariable: boolean;
      canCreateFunction: boolean;
    } | null>(null);
    const [portConnectionMenu, setPortConnectionMenu] = useState<{
      mouseX: number;
      mouseY: number;
      anchor: BehaviorPortPosition;
      sourceNodeId: string;
      sourcePortId: string;
      sourcePort: Port;
    } | null>(null);
    const [selectionRect, setSelectionRect] = useState<{
      start: BehaviorPortPosition;
      current: BehaviorPortPosition;
      additive: boolean;
    } | null>(null);
    const [portsLayoutTick, setPortsLayoutTick] = useState(0);
    const [hasBehaviorClipboardPayload, setHasBehaviorClipboardPayload] =
      useState(() =>
        isClipboardPayloadForScope(getInMemoryClipboardPayload(), "behavior"),
      );
    const lastCursorWorldPositionRef = useRef<{ x: number; y: number } | null>(
      null,
    );
    const canvasRef = useRef<HTMLDivElement | null>(null);
    const portsRefreshScheduledRef = useRef(false);
    const nodeElementsRef = useRef<Map<string, HTMLElement | null>>(new Map());
    const latestControlledNodesRef = useRef(nodes);
    const mouseMoveRafRef = useRef<number | null>(null);
    const pendingMouseClientRef = useRef<{ x: number; y: number } | null>(null);
    const handledConnectionDropRef = useRef(false);
    useEffect(() => {
      setInternalNodes(nodes);
    }, [nodes]);
    useEffect(() => {
      latestControlledNodesRef.current = nodes;
    }, [nodes]);

    const viewportOffset = viewport?.offset ?? internalViewportOffset;
    const viewportZoom = viewport?.zoom ?? internalViewportZoom;
    const viewportMetricsRef = useRef({
      offsetX: viewportOffset.x,
      offsetY: viewportOffset.y,
      zoom: viewportZoom,
    });
    viewportMetricsRef.current.offsetX = viewportOffset.x;
    viewportMetricsRef.current.offsetY = viewportOffset.y;
    viewportMetricsRef.current.zoom = viewportZoom;

    const updateViewport = useCallback(
      (nextViewport: BehaviorViewportState) => {
        if (!viewport) {
          setInternalViewportOffset(nextViewport.offset);
          setInternalViewportZoom(nextViewport.zoom);
        }
        onViewportChange?.(nextViewport);
      },
      [onViewportChange, viewport],
    );

    useEffect(() => {
      const updateModifierState = (event: KeyboardEvent) => {
        setIsDeleteModifierPressed(event.ctrlKey || event.metaKey);
      };

      const clearModifierState = () => {
        setIsDeleteModifierPressed(false);
      };

      window.addEventListener("keydown", updateModifierState);
      window.addEventListener("keyup", updateModifierState);
      window.addEventListener("blur", clearModifierState);

      return () => {
        window.removeEventListener("keydown", updateModifierState);
        window.removeEventListener("keyup", updateModifierState);
        window.removeEventListener("blur", clearModifierState);
      };
    }, []);

    const rawRenderedNodes = onNodesChange ? nodes : internalNodes;
    const renderedNodes = useMemo(
      () =>
        customObjectPathSchema
          ? synchronizeBehaviorObjectPaths(
              { nodes: rawRenderedNodes, connections },
              customObjectPathSchema,
            ).nodes
          : rawRenderedNodes,
      [rawRenderedNodes, connections, customObjectPathSchema],
    );

    const displayedNodes = useMemo(() => {
      const flowLabels = {
        flowIn: behaviorDico.actions.flowIn,
        flowOut: behaviorDico.actions.flowOut,
      };
      const variableLabels = {
        valuePort: behaviorDico.controls.value,
        refOutput: behaviorDico.controls.value,
      };
      let list = renderedNodes.map((n) => {
        if (
          n.type !== "operator" ||
          n.operator !== "constructObject" ||
          n.objectDefinition
        )
          return n;
        const entry = sceneCatalog?.objectTypes?.find(
          (t) => t.id === n.objectTypeId,
        );
        return entry && sceneCatalog
          ? {
              ...n,
              objectTypeName: entry.name,
              ports: sceneObjectConstructorPorts(entry, sceneCatalog, n.ports),
            }
          : n;
      });
      if (scope === "functionGraph" && editingFunctionId && sceneCatalog) {
        const entry = sceneCatalog.functions.find(
          (item) => item.id === editingFunctionId,
        );
        if (entry) {
          list = syncFunctionGraphTerminalPorts(
            { nodes: list, connections },
            entry,
            flowLabels,
          ).nodes;
        }
      }
      if (scope === "variableGraph" && editingVariableId && sceneCatalog) {
        const entry = sceneCatalog.variables.find(
          (item) => item.id === editingVariableId,
        );
        if (entry) {
          list = syncVariableGraphTerminalPorts(
            { nodes: list, connections },
            entry,
            {
              valuePort: variableLabels.valuePort,
              refOutput: variableLabels.refOutput,
            },
          ).nodes;
        }
      }
      if (!sceneCatalog) return list;
      let synced = syncCallNodePortsInGraph(
        { nodes: list, connections: [] },
        sceneCatalog.functions,
        flowLabels,
        { filterConnections: false },
      ).nodes;
      synced = syncVariableRefNodePortsInGraph(
        { nodes: synced, connections: [] },
        sceneCatalog.variables,
        { refOutput: variableLabels.refOutput },
        { filterConnections: false },
      ).nodes;
      synced = syncSetVariableNodePortsInGraph(
        { nodes: synced, connections: [] },
        sceneCatalog.variables,
        {
          flowIn: flowLabels.flowIn,
          flowOut: flowLabels.flowOut,
          value: behaviorDico.actions.setVariableValue,
        },
        { filterConnections: false },
      ).nodes;
      return synced;
    }, [
      behaviorDico.actions.flowIn,
      behaviorDico.actions.flowOut,
      behaviorDico.actions.setVariableValue,
      behaviorDico.controls.value,
      connections,
      editingFunctionId,
      editingVariableId,
      renderedNodes,
      sceneCatalog,
      scope,
    ]);

    const displayedConnections = useMemo(() => {
      const flowLabels = {
        flowIn: behaviorDico.actions.flowIn,
        flowOut: behaviorDico.actions.flowOut,
      };
      const variableLabels = {
        valuePort: behaviorDico.controls.value,
        refOutput: behaviorDico.controls.value,
      };
      if (scope === "functionGraph" && editingFunctionId && sceneCatalog) {
        const entry = sceneCatalog.functions.find(
          (item) => item.id === editingFunctionId,
        );
        if (entry) {
          return syncFunctionGraphTerminalPorts(
            { nodes: renderedNodes, connections },
            entry,
            flowLabels,
          ).connections;
        }
      }
      if (scope === "variableGraph" && editingVariableId && sceneCatalog) {
        const entry = sceneCatalog.variables.find(
          (item) => item.id === editingVariableId,
        );
        if (entry) {
          return syncVariableGraphTerminalPorts(
            { nodes: renderedNodes, connections },
            entry,
            {
              valuePort: variableLabels.valuePort,
              refOutput: variableLabels.refOutput,
            },
          ).connections;
        }
      }
      return connections;
    }, [
      behaviorDico.actions.flowIn,
      behaviorDico.actions.flowOut,
      behaviorDico.controls.value,
      connections,
      editingFunctionId,
      editingVariableId,
      renderedNodes,
      sceneCatalog,
      scope,
    ]);

    const layoutListBindingSource = useMemo(
      () => layoutListBindingSourceProp ?? resolveLayoutListBindingSource(node),
      [layoutListBindingSourceProp, node],
    );
    const templateCatalog = useMemo(
      () =>
        getBehaviorTemplateCatalog({
          node,
          scope,
          allowRowIndexVariable,
          layoutListBindingSource,
          sceneGraphNodes:
            scope === "scene" ||
            scope === "triggerGraph" ||
            scope === "functionGraph" ||
            scope === "variableGraph"
              ? renderedNodes
              : undefined,
        }),
      [
        allowRowIndexVariable,
        layoutListBindingSource,
        node,
        renderedNodes,
        scope,
      ],
    );
    const sceneCatalogReferenceFilter = useMemo(
      () => ({
        excludeCustomEventId: editingCustomEventId,
        editingObjectTypeId,
        valueOnly: scope === "objectGraph",
      }),
      [editingCustomEventId, editingObjectTypeId, scope],
    );
    const sceneCatalogTemplateItems = useMemo(
      () =>
        showSceneCatalogPanel && sceneCatalog
          ? getSceneCatalogTemplateCatalogItems(
              sceneCatalog,
              catalogPanelSections,
              sceneCatalogReferenceFilter,
            )
          : [],
      [
        catalogPanelSections,
        sceneCatalog,
        sceneCatalogReferenceFilter,
        showSceneCatalogPanel,
      ],
    );
    const portConnectionTemplateCatalog = useMemo(
      () => [...templateCatalog, ...sceneCatalogTemplateItems],
      [sceneCatalogTemplateItems, templateCatalog],
    );
    const nodesById = useMemo(
      () => new Map(displayedNodes.map((node) => [node.id, node])),
      [displayedNodes],
    );
    const portsByNodeId = useMemo(
      () =>
        new Map(
          displayedNodes.map((currentNode) => [
            currentNode.id,
            new Map(currentNode.ports.map((port) => [port.id, port])),
          ]),
        ),
      [displayedNodes],
    );
    const deleteConnectionCursor = useMemo(() => {
      const cursorSvg = Scissors.trim();
      return `url("data:image/svg+xml,${encodeURIComponent(cursorSvg)}") 18 6, auto`;
    }, []);
    const updateNodes = useCallback(
      (updater: (current: BehaviorNode[]) => BehaviorNode[]) => {
        if (onNodesChange) {
          onNodesChange(updater(latestControlledNodesRef.current));
          return;
        }
        setInternalNodes((current) => updater(current));
      },
      [onNodesChange],
    );

    const commitFunctionSignature = useCallback(
      (
        inputs: import("../display-editor/layout-types").SceneFunctionPort[],
        outputs: import("../display-editor/layout-types").SceneFunctionPort[],
        nextNodes: BehaviorNode[],
        nextConnections: BehaviorConnection[],
      ) => {
        if (!editingFunctionId || !onFunctionPortsCommit) return;
        onFunctionPortsCommit(editingFunctionId, {
          inputs,
          outputs,
          nodes: nextNodes,
          connections: nextConnections,
        });
      },
      [editingFunctionId, onFunctionPortsCommit],
    );

    const handleAddFunctionInput = useCallback(
      (valueType: string, label: string) => {
        if (!editingFunctionId || !sceneCatalog) return;
        const entry = sceneCatalog.functions.find(
          (item) => item.id === editingFunctionId,
        );
        if (!entry) return;
        const portId = crypto.randomUUID();
        const inputs = [...entry.inputs, { id: portId, label, valueType }];
        const synced = syncFunctionGraphTerminalPorts(
          { nodes: renderedNodes, connections },
          { inputs, outputs: entry.outputs },
          {
            flowIn: behaviorDico.actions.flowIn,
            flowOut: behaviorDico.actions.flowOut,
          },
        );
        commitFunctionSignature(
          inputs,
          entry.outputs,
          synced.nodes,
          synced.connections,
        );
      },
      [
        behaviorDico.actions.flowIn,
        behaviorDico.actions.flowOut,
        behaviorDico.controls.value,
        commitFunctionSignature,
        connections,
        editingFunctionId,
        renderedNodes,
        sceneCatalog,
      ],
    );

    const handleRemoveFunctionInput = useCallback(
      (portId: string) => {
        if (!editingFunctionId || !sceneCatalog) return;
        const entry = sceneCatalog.functions.find(
          (item) => item.id === editingFunctionId,
        );
        if (!entry) return;
        const inputs = entry.inputs.filter((port) => port.id !== portId);
        const synced = syncFunctionGraphTerminalPorts(
          { nodes: renderedNodes, connections },
          { inputs, outputs: entry.outputs },
          {
            flowIn: behaviorDico.actions.flowIn,
            flowOut: behaviorDico.actions.flowOut,
          },
        );
        commitFunctionSignature(
          inputs,
          entry.outputs,
          synced.nodes,
          synced.connections,
        );
      },
      [
        behaviorDico.actions.flowIn,
        behaviorDico.actions.flowOut,
        behaviorDico.controls.value,
        commitFunctionSignature,
        connections,
        editingFunctionId,
        renderedNodes,
        sceneCatalog,
      ],
    );

    const handleRenameFunctionInput = useCallback(
      (portId: string, label: string) => {
        if (!editingFunctionId || !sceneCatalog) return;
        const entry = sceneCatalog.functions.find(
          (item) => item.id === editingFunctionId,
        );
        if (!entry) return;
        const trimmed = label.trim();
        if (!trimmed) return;
        const inputs = entry.inputs.map((port) =>
          port.id === portId ? { ...port, label: trimmed } : port,
        );
        const synced = syncFunctionGraphTerminalPorts(
          { nodes: renderedNodes, connections },
          { inputs, outputs: entry.outputs },
          {
            flowIn: behaviorDico.actions.flowIn,
            flowOut: behaviorDico.actions.flowOut,
          },
        );
        commitFunctionSignature(
          inputs,
          entry.outputs,
          synced.nodes,
          synced.connections,
        );
      },
      [
        behaviorDico.actions.flowIn,
        behaviorDico.actions.flowOut,
        commitFunctionSignature,
        connections,
        editingFunctionId,
        renderedNodes,
        sceneCatalog,
      ],
    );

    const handleAddFunctionOutput = useCallback(
      (valueType: string, label: string) => {
        if (!editingFunctionId || !sceneCatalog) return;
        const entry = sceneCatalog.functions.find(
          (item) => item.id === editingFunctionId,
        );
        if (!entry) return;
        const portId = crypto.randomUUID();
        const outputs = [...entry.outputs, { id: portId, label, valueType }];
        const synced = syncFunctionGraphTerminalPorts(
          { nodes: renderedNodes, connections },
          { inputs: entry.inputs, outputs },
          {
            flowIn: behaviorDico.actions.flowIn,
            flowOut: behaviorDico.actions.flowOut,
          },
        );
        commitFunctionSignature(
          entry.inputs,
          outputs,
          synced.nodes,
          synced.connections,
        );
      },
      [
        behaviorDico.actions.flowIn,
        behaviorDico.actions.flowOut,
        behaviorDico.controls.value,
        commitFunctionSignature,
        connections,
        editingFunctionId,
        renderedNodes,
        sceneCatalog,
      ],
    );

    const handleRemoveFunctionOutput = useCallback(
      (portId: string) => {
        if (!editingFunctionId || !sceneCatalog) return;
        const entry = sceneCatalog.functions.find(
          (item) => item.id === editingFunctionId,
        );
        if (!entry) return;
        const outputs = entry.outputs.filter((port) => port.id !== portId);
        const synced = syncFunctionGraphTerminalPorts(
          { nodes: renderedNodes, connections },
          { inputs: entry.inputs, outputs },
          {
            flowIn: behaviorDico.actions.flowIn,
            flowOut: behaviorDico.actions.flowOut,
          },
        );
        commitFunctionSignature(
          entry.inputs,
          outputs,
          synced.nodes,
          synced.connections,
        );
      },
      [
        behaviorDico.actions.flowIn,
        behaviorDico.actions.flowOut,
        behaviorDico.controls.value,
        commitFunctionSignature,
        connections,
        editingFunctionId,
        renderedNodes,
        sceneCatalog,
      ],
    );

    const handleRenameFunctionOutput = useCallback(
      (portId: string, label: string) => {
        if (!editingFunctionId || !sceneCatalog) return;
        const entry = sceneCatalog.functions.find(
          (item) => item.id === editingFunctionId,
        );
        if (!entry) return;
        const trimmed = label.trim();
        if (!trimmed) return;
        const outputs = entry.outputs.map((port) =>
          port.id === portId ? { ...port, label: trimmed } : port,
        );
        const synced = syncFunctionGraphTerminalPorts(
          { nodes: renderedNodes, connections },
          { inputs: entry.inputs, outputs },
          {
            flowIn: behaviorDico.actions.flowIn,
            flowOut: behaviorDico.actions.flowOut,
          },
        );
        commitFunctionSignature(
          entry.inputs,
          outputs,
          synced.nodes,
          synced.connections,
        );
      },
      [
        behaviorDico.actions.flowIn,
        behaviorDico.actions.flowOut,
        commitFunctionSignature,
        connections,
        editingFunctionId,
        renderedNodes,
        sceneCatalog,
      ],
    );

    const commitVariableSignature = useCallback(
      (
        inputs: import("../display-editor/layout-types").SceneVariableInputPort[],
        outputValueType: VariableValueType,
        nextNodes: BehaviorNode[],
        nextConnections: BehaviorConnection[],
        outputObjectTypeId?: string,
      ) => {
        if (!editingVariableId || !onVariablePortsCommit) return;
        onVariablePortsCommit(editingVariableId, {
          inputs,
          outputValueType,
          outputObjectTypeId:
            outputObjectTypeId ??
            sceneCatalog?.variables.find((v) => v.id === editingVariableId)
              ?.outputObjectTypeId,
          nodes: nextNodes,
          connections: nextConnections,
        });
      },
      [editingVariableId, onVariablePortsCommit, sceneCatalog],
    );

    const handleAddVariableInput = useCallback(
      (valueType: string, label: string) => {
        if (!editingVariableId || !sceneCatalog) return;
        const entry = sceneCatalog.variables.find(
          (item) => item.id === editingVariableId,
        );
        if (!entry) return;
        const portId = crypto.randomUUID();
        const inputs = [
          ...entry.inputs,
          {
            id: portId,
            label,
            valueType: valueType.split("#")[0] as VariableValueType,
            ...(valueType.includes("#")
              ? { objectTypeId: valueType.split("#")[1] }
              : {}),
          },
        ];
        const synced = syncVariableGraphTerminalPorts(
          { nodes: renderedNodes, connections },
          {
            inputs,
            outputValueType: entry.outputValueType,
            outputObjectTypeId: entry.outputObjectTypeId,
          },
          {
            valuePort: behaviorDico.controls.value,
            refOutput: behaviorDico.controls.value,
          },
        );
        commitVariableSignature(
          inputs,
          entry.outputValueType,
          synced.nodes,
          synced.connections,
        );
      },
      [
        behaviorDico.controls.value,
        behaviorDico.controls.value,
        commitVariableSignature,
        connections,
        editingVariableId,
        renderedNodes,
        sceneCatalog,
      ],
    );

    const handleRemoveVariableInput = useCallback(
      (portId: string) => {
        if (!editingVariableId || !sceneCatalog) return;
        const entry = sceneCatalog.variables.find(
          (item) => item.id === editingVariableId,
        );
        if (!entry) return;
        const inputs = entry.inputs.filter((port) => port.id !== portId);
        const synced = syncVariableGraphTerminalPorts(
          { nodes: renderedNodes, connections },
          {
            inputs,
            outputValueType: entry.outputValueType,
            outputObjectTypeId: entry.outputObjectTypeId,
          },
          {
            valuePort: behaviorDico.controls.value,
            refOutput: behaviorDico.controls.value,
          },
        );
        commitVariableSignature(
          inputs,
          entry.outputValueType,
          synced.nodes,
          synced.connections,
        );
      },
      [
        behaviorDico.controls.value,
        behaviorDico.controls.value,
        commitVariableSignature,
        connections,
        editingVariableId,
        renderedNodes,
        sceneCatalog,
      ],
    );

    const handleRenameVariableInput = useCallback(
      (portId: string, label: string) => {
        if (!editingVariableId || !sceneCatalog) return;
        const entry = sceneCatalog.variables.find(
          (item) => item.id === editingVariableId,
        );
        if (!entry) return;
        const trimmed = label.trim();
        if (!trimmed) return;
        const inputs = entry.inputs.map((port) =>
          port.id === portId ? { ...port, label: trimmed } : port,
        );
        const synced = syncVariableGraphTerminalPorts(
          { nodes: renderedNodes, connections },
          {
            inputs,
            outputValueType: entry.outputValueType,
            outputObjectTypeId: entry.outputObjectTypeId,
          },
          {
            valuePort: behaviorDico.controls.value,
            refOutput: behaviorDico.controls.value,
          },
        );
        commitVariableSignature(
          inputs,
          entry.outputValueType,
          synced.nodes,
          synced.connections,
        );
      },
      [
        behaviorDico.controls.value,
        commitVariableSignature,
        connections,
        editingVariableId,
        renderedNodes,
        sceneCatalog,
      ],
    );

    const handleChangeVariableOutputType = useCallback(
      (valueType: string) => {
        if (!editingVariableId || !sceneCatalog) return;
        const entry = sceneCatalog.variables.find(
          (item) => item.id === editingVariableId,
        );
        if (!entry) return;
        const outputObjectTypeId = valueType.includes("#")
          ? valueType.split("#")[1]
          : undefined;
        const outputValueType = valueType.split("#")[0] as VariableValueType;
        const synced = syncVariableGraphTerminalPorts(
          { nodes: renderedNodes, connections },
          { inputs: entry.inputs, outputValueType, outputObjectTypeId },
          {
            valuePort: behaviorDico.controls.value,
            refOutput: behaviorDico.controls.value,
          },
        );
        commitVariableSignature(
          entry.inputs,
          outputValueType,
          synced.nodes,
          synced.connections,
          outputObjectTypeId ?? "",
        );
      },
      [
        behaviorDico.controls.value,
        behaviorDico.controls.value,
        commitVariableSignature,
        connections,
        editingVariableId,
        renderedNodes,
        sceneCatalog,
      ],
    );

    useEffect(() => {
      return () => {
        if (mouseMoveRafRef.current != null) {
          cancelAnimationFrame(mouseMoveRafRef.current);
          mouseMoveRafRef.current = null;
        }
      };
    }, []);

    const getRelativePointerPosition = (
      clientX: number,
      clientY: number,
    ): BehaviorPortPosition => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) {
        return { x: clientX, y: clientY };
      }
      return { x: clientX - rect.left, y: clientY - rect.top };
    };

    const getWorldPointerPosition = (
      clientX: number,
      clientY: number,
    ): BehaviorPortPosition => {
      const relative = getRelativePointerPosition(clientX, clientY);
      return {
        x: (relative.x - viewportOffset.x) / viewportZoom,
        y: (relative.y - viewportOffset.y) / viewportZoom,
      };
    };

    const handlePortMouseDown = (nodeId: string, port: Port) => {
      if (readOnly) return;
      if (port.direction !== "output") {
        return;
      }
      const node = nodesById.get(nodeId);
      if (!node) {
        return;
      }
      const fromPosition = getPortPosition(node, port);
      if (!fromPosition) {
        return;
      }
      setPortConnectionMenu(null);
      setDragConnection({ nodeId, port, to: fromPosition });
    };

    const getListItemTypeFromNode = (
      sourceNode: BehaviorNode | undefined,
    ): Exclude<VariableValueType, "list"> => {
      if (!sourceNode) return "string";
      if (
        sourceNode.type === "variable" &&
        sourceNode.valueType === "variableRef" &&
        sceneCatalog?.variables.find((v) => v.id === sourceNode.variableId)
          ?.outputObjectTypeId
      )
        return "object";
      if (sourceNode.type === "variable" && sourceNode.valueType === "list") {
        return sourceNode.listItemValueType ?? "string";
      }
      if (sourceNode.type === "operator") {
        return sourceNode.listItemValueType ?? "string";
      }
      return "string";
    };

    const getObjectContextFromNode = (
      sourceNode: BehaviorNode | undefined,
      portId?: string,
      seen = new Set<string>(),
    ): { objectSource?: DataSourceId; objectFieldPathBase?: string } => {
      if (!sourceNode || seen.has(sourceNode.id)) return {};
      seen.add(sourceNode.id);
      const schemaId =
        sourceNode.type === "variable" && sourceNode.valueType === "variableRef"
          ? sceneCatalog?.variables.find((v) => v.id === sourceNode.variableId)
              ?.outputObjectTypeId
          : sourceNode.ports.find(
              (p) => p.direction === "output" && (!portId || p.id === portId),
            )?.objectTypeId;
      if (schemaId)
        return { objectSource: ("@object:" + schemaId) as DataSourceId };
      if (
        sourceNode.type === "operator" &&
        (sourceNode.operator === "extendObject" ||
          sourceNode.operator === "setProperty" ||
          sourceNode.operator === "pickObject" ||
          sourceNode.operator === "omitObject")
      ) {
        const edge = connections.find(
          (e) => e.toNodeId === sourceNode.id && e.toPortId === "input1",
        );
        const upstream =
          edge && renderedNodes.find((n) => n.id === edge.fromNodeId);
        if (upstream && upstream.id !== sourceNode.id)
          return getObjectContextFromNode(upstream, edge.fromPortId, seen);
      }
      if (sourceNode.type === "variable" && sourceNode.valueType === "object") {
        if (
          sourceNode.objectSource === LAYOUT_LIST_ROW_ITEM_OBJECT_SOURCE ||
          sourceNode.objectSource === LAYOUT_LIST_COMPARATOR_OBJECT_SOURCE
        ) {
          return { objectSource: layoutListBindingSource };
        }
        return {
          objectSource: sourceNode.objectSource as DataSourceId | undefined,
        };
      }
      if (
        sourceNode.type === "operator" &&
        sourceNode.mode === "unary" &&
        sourceNode.operator === "get"
      ) {
        const base =
          sourceNode.objectFieldPathBase && sourceNode.objectFieldPath
            ? `${sourceNode.objectFieldPathBase}.${sourceNode.objectFieldPath}`
            : sourceNode.objectFieldPath;
        if (
          sourceNode.objectSource === LAYOUT_LIST_ROW_ITEM_OBJECT_SOURCE ||
          sourceNode.objectSource === LAYOUT_LIST_COMPARATOR_OBJECT_SOURCE
        ) {
          return {
            objectSource: layoutListBindingSource,
            objectFieldPathBase: base,
          };
        }
        return {
          objectSource: sourceNode.objectSource as DataSourceId | undefined,
          objectFieldPathBase: base,
        };
      }
      // Binary list[index] `get`: output is a full element; carries `objectSource`
      // when the list item type is `object` (e.g. athletes → athlete fields).
      if (
        sourceNode.type === "operator" &&
        sourceNode.mode === "binary" &&
        (sourceNode.operator === "mapItems" ||
          sourceNode.operator === "objectEntries" ||
          sourceNode.operator === "get" ||
          sourceNode.operator === "find" ||
          sourceNode.operator === "findByCondition") &&
        sourceNode.objectSource
      ) {
        return {
          objectSource: sourceNode.objectSource as DataSourceId,
          objectFieldPathBase: undefined,
        };
      }
      // Unary `currentItem` / `comparator`: output(s) carry `objectSource`
      // when the iterated list is a list of objects. `comparator` exposes
      // two ports (A and B) but both share the same `objectSource`.
      if (
        sourceNode.type === "operator" &&
        sourceNode.mode === "unary" &&
        (sourceNode.operator === "currentItem" ||
          sourceNode.operator === "currentEntry" ||
          sourceNode.operator === "comparator") &&
        sourceNode.objectSource
      ) {
        return {
          objectSource: sourceNode.objectSource as DataSourceId,
          objectFieldPathBase: undefined,
        };
      }
      if (
        sourceNode.type === "operator" &&
        sourceNode.mode === "ternary" &&
        sourceNode.operator === "findByPath" &&
        sourceNode.objectSource
      ) {
        return {
          objectSource: sourceNode.objectSource as DataSourceId,
          objectFieldPathBase: undefined,
        };
      }
      return {};
    };

    const getObjectSourceFromListNode = (
      sourceNode: BehaviorNode | undefined,
    ): DataSourceId | undefined => {
      if (!sourceNode) return undefined;
      if (
        sourceNode.type === "variable" &&
        sourceNode.valueType === "variableRef"
      ) {
        const type = sceneCatalog?.variables.find(
          (v) => v.id === sourceNode.variableId,
        )?.outputObjectTypeId;
        if (type) return ("@object:" + type) as DataSourceId;
      }
      if (
        sourceNode.type === "variable" &&
        sourceNode.valueType === "list" &&
        sourceNode.objectSource &&
        (getPathSchema(sourceNode.objectSource)?.length ?? 0) > 0
      ) {
        return sourceNode.objectSource as DataSourceId;
      }
      if (
        sourceNode.type === "operator" &&
        (sourceNode.operator === "mapItems" ||
          sourceNode.operator === "objectEntries" ||
          sourceNode.operator === "get" ||
          sourceNode.operator === "find" ||
          sourceNode.operator === "findByPath" ||
          sourceNode.operator === "findByCondition" ||
          sourceNode.operator === "filter" ||
          sourceNode.operator === "sortBy" ||
          sourceNode.operator === "sort" ||
          sourceNode.operator === "take")
      ) {
        return sourceNode.objectSource as DataSourceId | undefined;
      }
      return undefined;
    };

    const applyListTypingFromConnection = (
      currentNodes: BehaviorNode[],
      connection: BehaviorConnection,
    ): BehaviorNode[] => {
      const fromNode = currentNodes.find(
        (node) => node.id === connection.fromNodeId,
      );
      const toNode = currentNodes.find(
        (node) => node.id === connection.toNodeId,
      );
      const fromPort = fromNode?.ports.find(
        (port) => port.id === connection.fromPortId,
      );
      if (
        toNode?.type === "operator" &&
        toNode.operator === "mapItems" &&
        connection.toPortId === "input2" &&
        fromPort
      ) {
        const context = getObjectContextFromNode(fromNode, fromPort?.id);
        return currentNodes.map((n) =>
          n.id === toNode.id && n.type === "operator"
            ? {
                ...n,
                listItemValueType: (fromPort.valueType === "list"
                  ? "object"
                  : fromPort.valueType) as Exclude<VariableValueType, "list">,
                objectSource: context.objectSource,
                ports: n.ports.map((p) =>
                  p.id === "output"
                    ? { ...p, objectTypeId: fromPort.objectTypeId }
                    : p,
                ),
              }
            : n,
        );
      }
      if (
        !toNode ||
        toNode.type !== "operator" ||
        fromPort?.valueType !== "list"
      ) {
        return currentNodes;
      }

      if (connection.toPortId !== "input1") {
        return currentNodes;
      }

      const listItemType = getListItemTypeFromNode(fromNode);
      const listObjectSource = getObjectSourceFromListNode(fromNode);
      const shouldRetypeOperator =
        toNode.operator === "push" ||
        toNode.operator === "get" ||
        toNode.operator === "remove" ||
        toNode.operator === "find" ||
        toNode.operator === "findByPath" ||
        toNode.operator === "findByCondition" ||
        toNode.operator === "filter" ||
        toNode.operator === "sortBy" ||
        toNode.operator === "sort" ||
        toNode.operator === "take" ||
        toNode.operator === "currentItem" ||
        toNode.operator === "comparator";
      if (!shouldRetypeOperator) {
        return currentNodes;
      }

      return currentNodes.map((node) => {
        if (node.id !== toNode.id || node.type !== "operator") return node;

        if (node.operator === "push") {
          return {
            ...node,
            listItemValueType: listItemType,
            ports: node.ports.map((port) =>
              port.id === "input2"
                ? { ...port, valueType: listItemType, value: undefined }
                : port,
            ),
          };
        }

        if (node.operator === "remove") {
          return {
            ...node,
            listItemValueType: listItemType,
            ports: node.ports.map((port) =>
              port.id === "output"
                ? { ...port, valueType: "list", value: [] as ListValue }
                : port,
            ),
          };
        }

        if (node.operator === "get") {
          return {
            ...node,
            objectSource:
              listItemType === "object" ? listObjectSource : undefined,
            objectFieldPathBase:
              listItemType === "object" ? undefined : node.objectFieldPathBase,
            objectFieldPath: undefined,
            listItemValueType: listItemType,
            ports: node.ports.map((port) =>
              port.id === "output"
                ? { ...port, valueType: listItemType, value: undefined }
                : port,
            ),
          };
        }

        if (node.operator === "find") {
          const fieldOptions =
            listItemType === "object" && listObjectSource
              ? getFlattenedPathOptions(
                  getPathSchemaAtPath(
                    getPathSchema(listObjectSource),
                    undefined,
                  ),
                  { includeChildren: false },
                )
              : [];
          const nextFieldPath =
            listItemType === "object" && listObjectSource
              ? fieldOptions.some((o) => o.value === node.objectFieldPath)
                ? node.objectFieldPath
                : fieldOptions[0]?.value
              : undefined;
          const input2ValueType =
            listItemType === "object" && listObjectSource && nextFieldPath
              ? getObjectFieldValueType(
                  listObjectSource,
                  nextFieldPath,
                  undefined,
                )
              : listItemType;

          return {
            ...node,
            objectSource:
              listItemType === "object" ? listObjectSource : undefined,
            objectFieldPath: nextFieldPath,
            objectFieldPathBase: undefined,
            listItemValueType: listItemType,
            ports: node.ports.map((port) => {
              // Preserve user-typed literal values when the port `valueType`
              // is unchanged: reconnecting the same list type to `input1`
              // must NOT wipe the value the user typed in `input2`.
              if (port.id === "output") {
                return port.valueType === listItemType
                  ? port
                  : { ...port, valueType: listItemType, value: undefined };
              }
              if (port.id === "input2") {
                return port.valueType === input2ValueType
                  ? port
                  : { ...port, valueType: input2ValueType, value: undefined };
              }
              return port;
            }),
          };
        }

        if (node.operator === "findByPath") {
          return {
            ...node,
            objectSource:
              listItemType === "object" ? listObjectSource : undefined,
            listItemValueType: listItemType,
            ports: node.ports.map((port) =>
              port.id === "output"
                ? port.valueType === listItemType
                  ? port
                  : { ...port, valueType: listItemType, value: undefined }
                : port,
            ),
          };
        }

        if (node.operator === "findByCondition") {
          return {
            ...node,
            objectSource:
              listItemType === "object" ? listObjectSource : undefined,
            listItemValueType: listItemType,
            ports: node.ports.map((port) =>
              port.id === "output"
                ? port.valueType === listItemType
                  ? port
                  : { ...port, valueType: listItemType, value: undefined }
                : port,
            ),
          };
        }

        if (node.operator === "currentItem") {
          return {
            ...node,
            objectSource:
              listItemType === "object" ? listObjectSource : undefined,
            listItemValueType: listItemType,
            ports: node.ports.map((port) =>
              port.id === "output"
                ? port.valueType === listItemType
                  ? port
                  : { ...port, valueType: listItemType, value: undefined }
                : port,
            ),
          };
        }

        if (node.operator === "comparator") {
          // Both `outputA` and `outputB` carry the iterated item type.
          return {
            ...node,
            objectSource:
              listItemType === "object" ? listObjectSource : undefined,
            listItemValueType: listItemType,
            ports: node.ports.map((port) =>
              port.id === "outputA" || port.id === "outputB"
                ? port.valueType === listItemType
                  ? port
                  : { ...port, valueType: listItemType, value: undefined }
                : port,
            ),
          };
        }

        if (
          node.operator === "filter" ||
          node.operator === "sortBy" ||
          node.operator === "sort" ||
          node.operator === "take"
        ) {
          // Output stays a `list`, but its `listItemValueType` follows the
          // upstream list type, so downstream nodes (e.g. another `filter`,
          // `currentItem`, `comparator`) infer the right item type.
          return {
            ...node,
            objectSource:
              listItemType === "object" ? listObjectSource : undefined,
            listItemValueType: listItemType,
            ports: node.ports.map((port) =>
              port.id === "output"
                ? port.valueType === "list"
                  ? port
                  : { ...port, valueType: "list", value: [] as ListValue }
                : port,
            ),
          };
        }

        return node;
      });
    };

    const applyObjectGetTypingFromConnection = (
      currentNodes: BehaviorNode[],
      connection: BehaviorConnection,
    ): BehaviorNode[] => {
      if (connection.toPortId !== "input1") {
        return currentNodes;
      }
      const fromNode = currentNodes.find(
        (node) => node.id === connection.fromNodeId,
      );
      const toNode = currentNodes.find(
        (node) => node.id === connection.toNodeId,
      );
      const fromPort = fromNode?.ports.find(
        (port) => port.id === connection.fromPortId,
      );
      if (
        fromPort?.valueType !== "object" ||
        !toNode ||
        toNode.type !== "operator"
      ) {
        return currentNodes;
      }
      if (toNode.mode !== "unary" || toNode.operator !== "get") {
        return currentNodes;
      }
      const objectContext = getObjectContextFromNode(fromNode, fromPort?.id);
      const objectSource = objectContext.objectSource;
      if (!objectSource) {
        return currentNodes;
      }
      const options = getFlattenedPathOptions(
        getPathSchemaAtPath(
          getPathSchema(objectSource),
          objectContext.objectFieldPathBase,
        ),
        { includeChildren: false },
      );
      const hasCurrentFieldPath = options.some(
        (option) => option.value === toNode.objectFieldPath,
      );
      const nextFieldPath = hasCurrentFieldPath
        ? toNode.objectFieldPath
        : options[0]?.value;
      const nextValueType = getObjectFieldValueType(
        objectSource,
        nextFieldPath,
        objectContext.objectFieldPathBase,
      );
      return currentNodes.map((node) => {
        if (node.id !== toNode.id || node.type !== "operator") {
          return node;
        }
        return {
          ...node,
          objectSource,
          objectFieldPathBase: objectContext.objectFieldPathBase,
          objectFieldPath: nextFieldPath,
          ports: node.ports.map((port) =>
            port.id === "output"
              ? { ...port, valueType: nextValueType, value: undefined }
              : port,
          ),
        };
      });
    };

    const synchronizeObjectGetNodes = (
      currentNodes: BehaviorNode[],
      currentConnections: BehaviorConnection[],
    ): BehaviorNode[] => {
      return currentNodes.map((node) => {
        if (
          node.type !== "operator" ||
          node.mode !== "unary" ||
          node.operator !== "get"
        ) {
          return node;
        }
        const inputConnection = currentConnections.find(
          (connection) =>
            connection.toNodeId === node.id && connection.toPortId === "input1",
        );
        if (!inputConnection) {
          // Keep objectSource / field path so the field dropdown in UnaryOperatorNode
          // stays available after unplugging; runtime still resolves from the wire when connected.
          return node;
        }
        const fromNode = currentNodes.find(
          (candidate) => candidate.id === inputConnection.fromNodeId,
        );
        const fromPort = fromNode?.ports.find(
          (port) => port.id === inputConnection.fromPortId,
        );
        if (fromPort?.valueType !== "object") {
          return {
            ...node,
            objectSource: undefined,
            objectFieldPathBase: undefined,
            objectFieldPath: undefined,
          };
        }
        const objectContext = getObjectContextFromNode(fromNode, fromPort?.id);
        const objectSource = objectContext.objectSource;
        if (!objectSource) {
          return {
            ...node,
            objectSource: undefined,
            objectFieldPathBase: undefined,
            objectFieldPath: undefined,
          };
        }
        const options = getFlattenedPathOptions(
          getPathSchemaAtPath(
            getPathSchema(objectSource),
            objectContext.objectFieldPathBase,
          ),
          { includeChildren: false },
        );
        const hasCurrentFieldPath = options.some(
          (option) => option.value === node.objectFieldPath,
        );
        const nextFieldPath = hasCurrentFieldPath
          ? node.objectFieldPath
          : options[0]?.value;
        const nextValueType = getObjectFieldValueType(
          objectSource,
          nextFieldPath,
          objectContext.objectFieldPathBase,
        );
        return {
          ...node,
          objectSource,
          objectFieldPathBase: objectContext.objectFieldPathBase,
          objectFieldPath: nextFieldPath,
          ports: node.ports.map((port) =>
            port.id === "output"
              ? { ...port, valueType: nextValueType, value: undefined }
              : port,
          ),
        };
      });
    };

    const synchronizeListFindOperators = (
      currentNodes: BehaviorNode[],
      currentConnections: BehaviorConnection[],
    ): BehaviorNode[] => {
      return currentNodes.map((node) => {
        if (
          node.type !== "operator" ||
          (node.operator !== "find" &&
            node.operator !== "findByPath" &&
            node.operator !== "findByCondition" &&
            node.operator !== "filter" &&
            node.operator !== "sort" &&
            node.operator !== "sortBy" &&
            node.operator !== "currentItem" &&
            node.operator !== "comparator")
        ) {
          return node;
        }

        const inputConnection = currentConnections.find(
          (connection) =>
            connection.toNodeId === node.id && connection.toPortId === "input1",
        );
        if (!inputConnection) {
          // No upstream list connected → fall back to default `string` item
          // type. This block runs on EVERY graph commit (incl. connecting /
          // disconnecting unrelated ports like `output`), so we must
          // preserve any port whose `valueType` is already at its default to
          // avoid wiping the user's literal values (e.g. typed `input2`).
          if (node.operator === "find") {
            return {
              ...node,
              objectSource: undefined,
              objectFieldPath: undefined,
              listItemValueType: "string",
              ports: node.ports.map((port) => {
                if (port.id === "output") {
                  return port.valueType === "string"
                    ? port
                    : { ...port, valueType: "string", value: undefined };
                }
                if (port.id === "input2") {
                  return port.valueType === "string"
                    ? port
                    : { ...port, valueType: "string", value: undefined };
                }
                return port;
              }),
            };
          }
          if (node.operator === "comparator") {
            return {
              ...node,
              objectSource: undefined,
              listItemValueType: "string",
              ports: node.ports.map((port) =>
                port.id === "outputA" || port.id === "outputB"
                  ? port.valueType === "string"
                    ? port
                    : { ...port, valueType: "string", value: undefined }
                  : port,
              ),
            };
          }
          if (
            node.operator === "filter" ||
            node.operator === "sort" ||
            node.operator === "sortBy"
          ) {
            return {
              ...node,
              objectSource: undefined,
              listItemValueType: "string",
              ports: node.ports.map((port) =>
                port.id === "output"
                  ? port.valueType === "list"
                    ? port
                    : { ...port, valueType: "list", value: [] as ListValue }
                  : port,
              ),
            };
          }
          // `findByPath` and `findByCondition` only retype the `output` port.
          // Their secondary inputs (string / boolean) stay unchanged.
          return {
            ...node,
            objectSource: undefined,
            listItemValueType: "string",
            ports: node.ports.map((port) =>
              port.id === "output"
                ? port.valueType === "string"
                  ? port
                  : { ...port, valueType: "string", value: undefined }
                : port,
            ),
          };
        }

        const fromNode = currentNodes.find(
          (candidate) => candidate.id === inputConnection.fromNodeId,
        );
        const fromPort = fromNode?.ports.find(
          (port) => port.id === inputConnection.fromPortId,
        );
        if (fromPort?.valueType !== "list") {
          return node;
        }

        const listItemType = getListItemTypeFromNode(fromNode);
        const listObjectSource = getObjectSourceFromListNode(fromNode);

        if (node.operator === "find") {
          const fieldOptions =
            listItemType === "object" && listObjectSource
              ? getFlattenedPathOptions(
                  getPathSchemaAtPath(
                    getPathSchema(listObjectSource),
                    undefined,
                  ),
                  { includeChildren: false },
                )
              : [];
          const nextFieldPath =
            listItemType === "object" && listObjectSource
              ? fieldOptions.some((o) => o.value === node.objectFieldPath)
                ? node.objectFieldPath
                : fieldOptions[0]?.value
              : undefined;
          const input2ValueType =
            listItemType === "object" && listObjectSource && nextFieldPath
              ? getObjectFieldValueType(
                  listObjectSource,
                  nextFieldPath,
                  undefined,
                )
              : listItemType;

          return {
            ...node,
            objectSource:
              listItemType === "object" ? listObjectSource : undefined,
            objectFieldPath: nextFieldPath,
            objectFieldPathBase: undefined,
            listItemValueType: listItemType,
            ports: node.ports.map((port) => {
              // Preserve any user-edited literal `value` when the port's
              // `valueType` is unchanged: this function runs on EVERY graph
              // commit (including connecting / disconnecting unrelated ports
              // like `output`), and reconstructing the port with
              // `value: undefined` would silently wipe whatever the user
              // typed in the `input2` field.
              if (port.id === "output") {
                return port.valueType === listItemType
                  ? port
                  : { ...port, valueType: listItemType, value: undefined };
              }
              if (port.id === "input2") {
                return port.valueType === input2ValueType
                  ? port
                  : { ...port, valueType: input2ValueType, value: undefined };
              }
              return port;
            }),
          };
        }

        if (node.operator === "comparator") {
          // Both A and B output ports follow the iterated item type. Preserve
          // the existing port when the type already matches to avoid spurious
          // commits on unrelated graph changes.
          return {
            ...node,
            objectSource:
              listItemType === "object" ? listObjectSource : undefined,
            listItemValueType: listItemType,
            ports: node.ports.map((port) =>
              port.id === "outputA" || port.id === "outputB"
                ? port.valueType === listItemType
                  ? port
                  : { ...port, valueType: listItemType, value: undefined }
                : port,
            ),
          };
        }

        if (
          node.operator === "filter" ||
          node.operator === "sort" ||
          node.operator === "sortBy"
        ) {
          // Output stays `list`; only `listItemValueType` follows the source
          // so downstream propagation (e.g. another `filter` / `currentItem`)
          // sees the right item type. The output port itself is always
          // `list` so no port reset is needed when nothing changes.
          return {
            ...node,
            objectSource:
              listItemType === "object" ? listObjectSource : undefined,
            listItemValueType: listItemType,
            ports: node.ports.map((port) =>
              port.id === "output"
                ? port.valueType === "list"
                  ? port
                  : { ...port, valueType: "list", value: [] as ListValue }
                : port,
            ),
          };
        }

        return {
          ...node,
          objectSource:
            listItemType === "object" ? listObjectSource : undefined,
          listItemValueType: listItemType,
          ports: node.ports.map((port) =>
            port.id === "output"
              ? port.valueType === listItemType
                ? port
                : { ...port, valueType: listItemType, value: undefined }
              : port,
          ),
        };
      });
    };

    const handlePortMouseUp = (nodeId: string, port: Port) => {
      handledConnectionDropRef.current = true;
      if (!dragConnection || port.direction !== "input") {
        return;
      }

      if (dragConnection.nodeId === nodeId) {
        setDragConnection(null);
        return;
      }

      if (
        !areBehaviorPortTypesCompatible(
          dragConnection.port.valueType,
          port.valueType,
        )
      ) {
        setDragConnection(null);
        return;
      }

      const newConnection: BehaviorConnection = {
        id: buildConnectionId(
          dragConnection.nodeId,
          dragConnection.port.id,
          nodeId,
          port.id,
        ),
        fromNodeId: dragConnection.nodeId,
        fromPortId: dragConnection.port.id,
        toNodeId: nodeId,
        toPortId: port.id,
      };

      const exists = connections.some(
        (connection) =>
          connection.id === newConnection.id ||
          (connection.toNodeId === newConnection.toNodeId &&
            connection.toPortId === newConnection.toPortId),
      );
      if (!exists) {
        const snapshot = buildConnectedGraphSnapshot(
          renderedNodes,
          [...connections, newConnection],
          newConnection,
        );
        commitBehaviorGraphSnapshot(snapshot);
      }
      setDragConnection(null);
    };

    const handleMouseMove: MouseEventHandler<HTMLDivElement> = (event) => {
      pendingMouseClientRef.current = { x: event.clientX, y: event.clientY };
      if (mouseMoveRafRef.current != null) return;
      mouseMoveRafRef.current = requestAnimationFrame(() => {
        mouseMoveRafRef.current = null;
        const pointerClient = pendingMouseClientRef.current;
        if (!pointerClient) return;
        lastCursorWorldPositionRef.current = getWorldPointerPosition(
          pointerClient.x,
          pointerClient.y,
        );
        if (dragViewport) {
          updateViewport({
            offset: {
              x:
                dragViewport.startOffsetX +
                (pointerClient.x - dragViewport.startClientX),
              y:
                dragViewport.startOffsetY +
                (pointerClient.y - dragViewport.startClientY),
            },
            zoom: viewportZoom,
          });
          return;
        }
        if (dragNode) {
          const pointer = getWorldPointerPosition(
            pointerClient.x,
            pointerClient.y,
          );
          const deltaX = pointer.x - dragNode.startPointer.x;
          const deltaY = pointer.y - dragNode.startPointer.y;
          updateNodes((current) =>
            current.map((currentNode) =>
              dragNode.nodeIds.includes(currentNode.id)
                ? {
                    ...currentNode,
                    x:
                      (dragNode.initialPositions[currentNode.id]?.x ??
                        currentNode.x) + deltaX,
                    y:
                      (dragNode.initialPositions[currentNode.id]?.y ??
                        currentNode.y) + deltaY,
                  }
                : currentNode,
            ),
          );
          return;
        }
        if (selectionRect) {
          const position = getWorldPointerPosition(
            pointerClient.x,
            pointerClient.y,
          );
          setSelectionRect((current) =>
            current ? { ...current, current: position } : current,
          );
          return;
        }
        if (!dragConnection) return;
        const position = getWorldPointerPosition(
          pointerClient.x,
          pointerClient.y,
        );
        setDragConnection((current) =>
          current ? { ...current, to: position } : current,
        );
      });
    };

    const handleCanvasMouseUp: MouseEventHandler<HTMLDivElement> = (event) => {
      const currentDragConnection = dragConnection;
      const dropWasHandled = handledConnectionDropRef.current;
      handledConnectionDropRef.current = false;
      if (dragNode) {
        setDragNode(null);
      }
      if (dragViewport) {
        setDragViewport(null);
      }
      if (currentDragConnection) {
        const target = event.target as HTMLElement | null;
        const releasedOnPortHandle = Boolean(
          target?.closest?.("[data-port-handle]"),
        );
        const shouldOpenConnectionPicker =
          !dropWasHandled &&
          event.type !== "mouseleave" &&
          !releasedOnPortHandle &&
          !readOnly;
        if (shouldOpenConnectionPicker) {
          const anchor =
            event.type === "mouseup"
              ? getWorldPointerPosition(event.clientX, event.clientY)
              : (lastCursorWorldPositionRef.current ??
                currentDragConnection.to);
          lastCursorWorldPositionRef.current = anchor;
          setPortConnectionMenu({
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
            anchor,
            sourceNodeId: currentDragConnection.nodeId,
            sourcePortId: currentDragConnection.port.id,
            sourcePort: currentDragConnection.port,
          });
        }
        setDragConnection(null);
      }
      if (selectionRect) {
        const minX = Math.min(selectionRect.start.x, selectionRect.current.x);
        const maxX = Math.max(selectionRect.start.x, selectionRect.current.x);
        const minY = Math.min(selectionRect.start.y, selectionRect.current.y);
        const maxY = Math.max(selectionRect.start.y, selectionRect.current.y);
        const selectedInRect = renderedNodes
          .filter((node) => {
            const element = nodeElementsRef.current.get(node.id);
            const canvas = canvasRef.current;
            if (!element || !canvas) {
              return false;
            }
            const elementRect = element.getBoundingClientRect();
            const canvasRect = canvas.getBoundingClientRect();
            const left =
              (elementRect.left - canvasRect.left - viewportOffset.x) /
              viewportZoom;
            const top =
              (elementRect.top - canvasRect.top - viewportOffset.y) /
              viewportZoom;
            const right =
              (elementRect.right - canvasRect.left - viewportOffset.x) /
              viewportZoom;
            const bottom =
              (elementRect.bottom - canvasRect.top - viewportOffset.y) /
              viewportZoom;
            return (
              left >= minX && top >= minY && right <= maxX && bottom <= maxY
            );
          })
          .map((node) => node.id);
        setSelectedNodeIds((previous) => {
          if (!selectionRect.additive) {
            return selectedInRect;
          }
          const merged = new Set([...previous, ...selectedInRect]);
          return [...merged];
        });
        setSelectionRect(null);
      }
    };

    const handleNodeMouseDown = (
      event: MouseEvent<HTMLDivElement>,
      nodeId: string,
    ) => {
      const target = event.target as HTMLElement;
      if (event.button !== 0) {
        return;
      }
      if (dragConnection) {
        return;
      }
      if (target.closest("[data-port-handle]")) {
        return;
      }
      const node = nodesById.get(nodeId);
      if (!node) {
        return;
      }
      const isAdditive = event.ctrlKey || event.metaKey;
      if (isAdditive) {
        setSelectedNodeIds((previous) =>
          previous.includes(nodeId)
            ? previous.filter((id) => id !== nodeId)
            : [...previous, nodeId],
        );
        return;
      }
      const nextSelectedNodeIds = selectedNodeIds.includes(nodeId)
        ? selectedNodeIds
        : [nodeId];
      setSelectedNodeIds(nextSelectedNodeIds);
      if (readOnly) return;
      const pointer = getWorldPointerPosition(event.clientX, event.clientY);
      const initialPositions: Record<string, { x: number; y: number }> = {};
      nextSelectedNodeIds.forEach((id) => {
        const selectedNode = nodesById.get(id);
        if (!selectedNode) return;
        initialPositions[id] = { x: selectedNode.x, y: selectedNode.y };
      });
      setDragNode({
        nodeIds: nextSelectedNodeIds,
        startPointer: pointer,
        initialPositions,
      });
    };

    const handleNodeDoubleClick = (
      event: MouseEvent<HTMLDivElement>,
      nodeId: string,
    ) => {
      event.stopPropagation();
      if (!onOpenSceneCatalogEntry) return;
      const target = event.target as HTMLElement;
      if (target.closest("[data-port-handle]")) return;
      const node = nodesById.get(nodeId);
      if (!node) return;
      const route = resolveSceneCatalogDetailRoute(node, sceneCatalog);
      if (!route) return;
      onOpenSceneCatalogEntry(route);
    };

    const handleCanvasMouseDown = (event: MouseEvent<HTMLDivElement>) => {
      lastCursorWorldPositionRef.current = getWorldPointerPosition(
        event.clientX,
        event.clientY,
      );
      if (dragConnection || dragNode) {
        return;
      }
      const target = event.target as HTMLElement;
      if (
        target.closest("[data-node-root]") ||
        target.closest("[data-port-id]")
      ) {
        return;
      }
      if (event.button === 1) {
        event.preventDefault();
        setSelectedNodeIds([]);
        setDragViewport({
          startClientX: event.clientX,
          startClientY: event.clientY,
          startOffsetX: viewportOffset.x,
          startOffsetY: viewportOffset.y,
        });
        return;
      }
      if (event.button === 0) {
        const start = getWorldPointerPosition(event.clientX, event.clientY);
        const additive = event.ctrlKey || event.metaKey;
        if (!additive) {
          setSelectedNodeIds([]);
        }
        setSelectionRect({
          start,
          current: start,
          additive,
        });
      }
    };

    const openBehaviorNodeMenu = useCallback(
      (mouseX: number, mouseY: number, selection: string[]) => {
        const graph = { nodes: renderedNodes, connections };
        const canCreateVariable =
          !readOnly &&
          scope === "node" &&
          Boolean(onCreateVariableFromSelection) &&
          Boolean(sceneCatalog) &&
          selection.length >= 2 &&
          analyzeSelectionForVariableExtraction(graph, selection) != null;
        const canCreateFunction =
          !readOnly &&
          scope === "node" &&
          Boolean(onCreateFunctionFromSelection) &&
          Boolean(sceneCatalog) &&
          selection.length >= 2 &&
          analyzeSelectionForFunctionExtraction(graph, selection) != null;

        setNodeMenu({
          mouseX,
          mouseY,
          selectionNodeIds: selection,
          canCreateVariable,
          canCreateFunction,
        });
      },
      [
        readOnly,
        scope,
        onCreateVariableFromSelection,
        onCreateFunctionFromSelection,
        sceneCatalog,
        renderedNodes,
        connections,
      ],
    );

    const handleNodeContextMenu = (
      event: MouseEvent<HTMLDivElement>,
      nodeId: string,
    ) => {
      lastCursorWorldPositionRef.current = getWorldPointerPosition(
        event.clientX,
        event.clientY,
      );
      event.preventDefault();
      event.stopPropagation();
      const isAdditive = event.ctrlKey || event.metaKey;
      const nextSelection = selectedNodeIds.includes(nodeId)
        ? selectedNodeIds
        : isAdditive
          ? [...selectedNodeIds, nodeId]
          : [nodeId];
      setSelectedNodeIds(nextSelection);
      openBehaviorNodeMenu(event.clientX + 2, event.clientY - 6, nextSelection);
    };

    const portsMapRef = useRef<Map<string, HTMLElement | null>>(new Map());
    /** Last full DOM measurement of port centers (world coords). Updated only on layout passes. */
    const lastMeasuredPortPositionsRef = useRef<
      Map<string, BehaviorPortPosition>
    >(new Map());
    const setPortsMap = useCallback(
      (nodeId: string, portId: string, element: HTMLElement | null) => {
        const key = `${nodeId}:${portId}`;
        const previous = portsMapRef.current.get(key);

        if (element === null) {
          portsMapRef.current.delete(key);
          return;
        }

        if (previous === element) {
          return;
        }

        portsMapRef.current.set(key, element);

        if (!portsRefreshScheduledRef.current) {
          portsRefreshScheduledRef.current = true;
          requestAnimationFrame(() => {
            portsRefreshScheduledRef.current = false;
            setPortsLayoutTick((current) => current + 1);
          });
        }
      },
      [],
    );

    const getPortPosition = useCallback(
      (node: BehaviorNode, port: Port): BehaviorPortPosition | undefined => {
        const element = portsMapRef.current.get(`${node.id}:${port.id}`);
        const canvasElement = canvasRef.current;
        if (element && canvasElement) {
          const rect = element.getBoundingClientRect();
          const canvasRect = canvasElement.getBoundingClientRect();
          const canvasX = rect.left + rect.width / 2 - canvasRect.left;
          const canvasY = rect.top + rect.height / 2 - canvasRect.top;
          const x =
            (canvasX - viewportMetricsRef.current.offsetX) /
            viewportMetricsRef.current.zoom;
          const y =
            (canvasY - viewportMetricsRef.current.offsetY) /
            viewportMetricsRef.current.zoom;
          return { x, y };
        }
        return undefined;
      },
      [],
    );
    const portPositionsByKey = useMemo(() => {
      void portsLayoutTick;
      const canvasElement = canvasRef.current;
      if (!canvasElement) return new Map<string, BehaviorPortPosition>();

      const measureAllPortsFromDom = () => {
        const canvasRect = canvasElement.getBoundingClientRect();
        const positions = new Map<string, BehaviorPortPosition>();
        displayedNodes.forEach((currentNode) => {
          currentNode.ports.forEach((port) => {
            const element = portsMapRef.current.get(
              `${currentNode.id}:${port.id}`,
            );
            if (!element) return;
            const rect = element.getBoundingClientRect();
            const canvasX = rect.left + rect.width / 2 - canvasRect.left;
            const canvasY = rect.top + rect.height / 2 - canvasRect.top;
            positions.set(`${currentNode.id}:${port.id}`, {
              x:
                (canvasX - viewportMetricsRef.current.offsetX) /
                viewportMetricsRef.current.zoom,
              y:
                (canvasY - viewportMetricsRef.current.offsetY) /
                viewportMetricsRef.current.zoom,
            });
          });
        });
        return positions;
      };

      if (dragNode && lastMeasuredPortPositionsRef.current.size > 0) {
        const base = lastMeasuredPortPositionsRef.current;
        const next = new Map(base);
        for (const nodeId of dragNode.nodeIds) {
          const currentNode = nodesById.get(nodeId);
          const initial = dragNode.initialPositions[nodeId];
          if (!currentNode || !initial) continue;
          const dx = currentNode.x - initial.x;
          const dy = currentNode.y - initial.y;
          for (const port of currentNode.ports) {
            const key = `${nodeId}:${port.id}`;
            const p = base.get(key);
            if (p) {
              next.set(key, { x: p.x + dx, y: p.y + dy });
            }
          }
        }
        return next;
      }

      const positions = measureAllPortsFromDom();
      lastMeasuredPortPositionsRef.current = positions;
      return positions;
    }, [displayedNodes, portsLayoutTick, dragNode, nodesById]);

    const getCurvePath = useCallback(
      (from: BehaviorPortPosition, to: BehaviorPortPosition) => {
        const dx = Math.max(40, Math.abs(to.x - from.x) * 0.45);
        return `M ${from.x} ${from.y} C ${from.x + dx} ${from.y}, ${to.x - dx} ${to.y}, ${to.x} ${to.y}`;
      },
      [],
    );

    const buildConnectionId = useCallback(
      (
        fromNodeId: string,
        fromPortId: string,
        toNodeId: string,
        toPortId: string,
      ) => `${fromNodeId}:${fromPortId}->${toNodeId}:${toPortId}`,
      [],
    );

    const inferVariableValueTypeFromValue = useCallback(
      (value: unknown): VariableValueType => {
        if (Array.isArray(value)) return "list";
        if (typeof value === "boolean") return "boolean";
        if (typeof value === "number") return "number";
        if (
          typeof value === "string" &&
          (ANIMATION_DIRECTION_VALUES as readonly string[]).includes(value)
        ) {
          return "animationDirection";
        }
        if (
          typeof value === "string" &&
          (ANIMATION_FILL_MODE_VALUES as readonly string[]).includes(value)
        ) {
          return "animationFill";
        }
        if (
          typeof value === "string" &&
          (ANIMATION_EASING_VALUES as readonly string[]).includes(value)
        ) {
          return "animationEasing";
        }
        if (typeof value === "string" && isColorString(value)) return "color";
        if (typeof value === "string" && isGradientString(value))
          return "gradient";
        if (typeof value === "string" && isFilterString(value)) return "filter";
        if (typeof value === "string" && isBoxShadowString(value))
          return "boxShadow";
        if (typeof value === "string" && isTextShadowString(value))
          return "textShadow";
        if (typeof value === "string") {
          const candidate = value.trim().replace(/^['"]|['"]$/g, "");
          if (
            isKnownFontFamilyValue(candidate) ||
            (customFontFamilies?.some(
              (family) => family.toLowerCase() === candidate.toLowerCase(),
            ) ??
              false)
          ) {
            return "fontFamily";
          }
        }
        if (
          typeof value === "string" &&
          normalizeFontWeightValue(value) === value.trim()
        ) {
          return "fontWeight";
        }
        if (
          typeof value === "string" &&
          normalizeTextAlignValue(value) === value.trim().toLowerCase()
        ) {
          return "textAlign";
        }
        if (
          typeof value === "string" &&
          normalizeTextTransformValue(value) === value.trim().toLowerCase()
        ) {
          return "textTransform";
        }
        if (typeof value === "string") {
          const trimmed = value.trim();
          if (
            (WHITE_SPACE_VALUES as readonly string[]).includes(trimmed) ||
            trimmed.toLowerCase() === "preserve nowrap"
          ) {
            return "whiteSpace";
          }
        }
        if (
          value &&
          typeof value === "object" &&
          "component" in (value as Record<string, unknown>) &&
          (value as { component?: unknown }).component === "barLoader"
        ) {
          return "node";
        }
        if (
          value &&
          typeof value === "object" &&
          "border" in (value as Record<string, unknown>)
        ) {
          const border = (value as { border?: unknown }).border;
          if (
            border &&
            typeof border === "object" &&
            ["top", "right", "bottom", "left"].every(
              (side) =>
                typeof (border as Record<string, unknown>)[side] === "string",
            )
          ) {
            return "border";
          }
        }
        if (
          value &&
          typeof value === "object" &&
          "radius" in (value as Record<string, unknown>)
        ) {
          const radius = (value as { radius?: unknown }).radius;
          if (
            radius &&
            typeof radius === "object" &&
            ["topLeft", "topRight", "bottomRight", "bottomLeft"].every(
              (corner) =>
                typeof (radius as Record<string, unknown>)[corner] === "string",
            )
          ) {
            return "borderRadius";
          }
        }
        if (
          value &&
          typeof value === "object" &&
          "margin" in (value as Record<string, unknown>) &&
          "padding" in (value as Record<string, unknown>)
        ) {
          const margin = (value as { margin?: unknown }).margin;
          const padding = (value as { padding?: unknown }).padding;
          if (
            margin &&
            typeof margin === "object" &&
            padding &&
            typeof padding === "object" &&
            "scope" in margin &&
            "value" in margin &&
            "scope" in padding &&
            "value" in padding
          ) {
            return "spacing";
          }
        }
        if (
          value instanceof Date ||
          (typeof value === "string" && isDateString(value))
        )
          return "date";
        if (typeof value === "string") return "string";
        if (value != null && typeof value === "object") {
          const numericCandidate = Number(
            (value as { toString?: () => string }).toString?.(),
          );
          if (!Number.isNaN(numericCandidate)) return "number";
          return "object";
        }
        return "string";
      },
      [customFontFamilies],
    );

    const getObjectFieldValueType = useCallback(
      (
        objectSource?: string,
        fieldPath?: string,
        objectFieldPathBase?: string,
      ): VariableValueType => {
        const isLayoutListRowContext =
          objectSource === LAYOUT_LIST_ROW_ITEM_OBJECT_SOURCE ||
          objectSource === LAYOUT_LIST_COMPARATOR_OBJECT_SOURCE;
        const resolvedObjectSource = isLayoutListRowContext
          ? layoutListBindingSource
          : objectSource;
        if (!resolvedObjectSource || !fieldPath) return "string";
        if (
          resolvedObjectSource === "currentNode" &&
          (fieldPath === "x" ||
            fieldPath === "y" ||
            fieldPath === "width" ||
            fieldPath === "height" ||
            fieldPath === "rotation" ||
            fieldPath === "skewX" ||
            fieldPath === "skewY")
        ) {
          return "number";
        }
        const pathSchemaValueType = getPathEntryValueType(
          getPathSchema(resolvedObjectSource),
          fieldPath,
          objectFieldPathBase,
        );
        if (pathSchemaValueType) {
          return pathSchemaValueType;
        }
        const schemaValueType = getSchemaValueTypeForFieldPath(
          resolvedObjectSource as DataSourceId,
          fieldPath,
          objectFieldPathBase,
        );
        if (schemaValueType) {
          return schemaValueType;
        }
        const mockData = getMockLayoutData();
        const rawSourceValue =
          mockData[resolvedObjectSource as keyof typeof mockData];
        const sourceValue = Array.isArray(rawSourceValue)
          ? rawSourceValue[0]
          : rawSourceValue;
        const resolvedFieldPath =
          objectFieldPathBase && fieldPath
            ? `${objectFieldPathBase}.${fieldPath}`
            : fieldPath;
        const fieldValue = resolvePath(sourceValue, resolvedFieldPath);
        return inferVariableValueTypeFromValue(fieldValue);
      },
      [getPathSchema, inferVariableValueTypeFromValue, layoutListBindingSource],
    );

    const buildNodeFromTemplate = (
      template: BehaviorDraggedNodeTemplate,
      x: number,
      y: number,
    ): BehaviorNode => {
      const id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `node-${Date.now()}`;
      const createInputPort = (
        portId: string,
        valueType: VariableValueType | "flow",
      ): Port => ({
        id: portId,
        label: portId,
        direction: "input" as const,
        valueType,
        value:
          valueType === "color"
            ? "#00000000"
            : valueType === "gradient"
              ? "linear-gradient(90deg, #000000 0%, #ffffff 100%)"
              : valueType === "filter"
                ? "none"
                : valueType === "boxShadow"
                  ? "none"
                  : valueType === "textShadow"
                    ? "none"
                    : valueType === "fontFamily"
                      ? "Arial"
                      : valueType === "fontWeight"
                        ? "normal"
                        : valueType === "textAlign"
                          ? "left"
                          : valueType === "textTransform"
                            ? "none"
                            : valueType === "whiteSpace"
                              ? "normal"
                              : valueType === "animationDirection"
                                ? "normal"
                                : valueType === "animationFill"
                                  ? "forwards"
                                  : valueType === "animationEasing"
                                    ? "ease"
                                    : valueType === "border"
                                      ? ({
                                          border: {
                                            top: "1px solid #000000",
                                            right: "1px solid #000000",
                                            bottom: "1px solid #000000",
                                            left: "1px solid #000000",
                                          },
                                        } satisfies BorderConfig)
                                      : valueType === "borderRadius"
                                        ? ({
                                            radius: {
                                              topLeft: "0px",
                                              topRight: "0px",
                                              bottomRight: "0px",
                                              bottomLeft: "0px",
                                            },
                                          } satisfies BorderRadiusConfig)
                                        : valueType === "spacing"
                                          ? ({
                                              margin: {
                                                scope: "all",
                                                value: 0,
                                              },
                                              padding: {
                                                scope: "all",
                                                value: 0,
                                              },
                                            } satisfies SpacingConfig)
                                          : valueType === "location"
                                            ? ({
                                                x: Math.round(node.x),
                                                y: Math.round(node.y),
                                                z: Math.round(node.zIndex),
                                              } satisfies LocationValue)
                                            : valueType === "transform"
                                              ? ({
                                                  width: Math.round(node.width),
                                                  height: Math.round(
                                                    node.height,
                                                  ),
                                                  rotation: Math.round(
                                                    node.rotation ?? 0,
                                                  ),
                                                } satisfies TransformValue)
                                              : valueType === "list"
                                                ? ([] satisfies ListValue)
                                                : valueType === "node"
                                                  ? undefined
                                                  : valueType ===
                                                      "layoutListTarget"
                                                    ? undefined
                                                    : undefined,
      });
      const createOutputPort = (
        valueType: VariableValueType | "flow",
      ): Port => ({
        id: "output",
        label: "",
        direction: "output" as const,
        valueType,
        value:
          valueType === "color"
            ? "#00000000"
            : valueType === "gradient"
              ? "linear-gradient(90deg, #000000 0%, #ffffff 100%)"
              : valueType === "filter"
                ? "none"
                : valueType === "boxShadow"
                  ? "none"
                  : valueType === "textShadow"
                    ? "none"
                    : valueType === "fontFamily"
                      ? "Arial"
                      : valueType === "fontWeight"
                        ? "normal"
                        : valueType === "textAlign"
                          ? "left"
                          : valueType === "textTransform"
                            ? "none"
                            : valueType === "whiteSpace"
                              ? "normal"
                              : valueType === "border"
                                ? ({
                                    border: {
                                      top: "1px solid #000000",
                                      right: "1px solid #000000",
                                      bottom: "1px solid #000000",
                                      left: "1px solid #000000",
                                    },
                                  } satisfies BorderConfig)
                                : valueType === "borderRadius"
                                  ? ({
                                      radius: {
                                        topLeft: "0px",
                                        topRight: "0px",
                                        bottomRight: "0px",
                                        bottomLeft: "0px",
                                      },
                                    } satisfies BorderRadiusConfig)
                                  : valueType === "spacing"
                                    ? ({
                                        margin: { scope: "all", value: 0 },
                                        padding: { scope: "all", value: 0 },
                                      } satisfies SpacingConfig)
                                    : valueType === "location"
                                      ? ({
                                          x: Math.round(node.x),
                                          y: Math.round(node.y),
                                          z: Math.round(node.zIndex),
                                        } satisfies LocationValue)
                                      : valueType === "transform"
                                        ? ({
                                            width: Math.round(node.width),
                                            height: Math.round(node.height),
                                            rotation: Math.round(
                                              node.rotation ?? 0,
                                            ),
                                          } satisfies TransformValue)
                                        : valueType === "list"
                                          ? ([] satisfies ListValue)
                                          : valueType === "node"
                                            ? undefined
                                            : valueType === "layoutListTarget"
                                              ? undefined
                                              : undefined,
      });
      if (
        template.type === "operator" &&
        template.operator === "constructObject"
      ) {
        const entry = sceneCatalog?.objectTypes?.find(
          (t) => t.id === template.objectTypeId,
        );
        if (entry && sceneCatalog)
          return createSceneObjectConstructor(entry, sceneCatalog, id, x, y);
      }
      if (template.type === "operator" && template.operator === "readTimer")
        return {
          id,
          x,
          y,
          type: "operator",
          mode: "unary",
          operator: "readTimer",
          ports: timerReadPorts(behaviorDico.timers),
        };
      if (template.type === "variable") {
        if ("variableId" in template) {
          const varRefTmpl =
            template as import("../../../util/src/blueprint/types.js").VariableRefNodeTemplate;
          const catalogEntry = sceneCatalog?.variables?.find(
            (v) => v.id === varRefTmpl.variableId,
          );
          return {
            id,
            x,
            y,
            type: "variable",
            valueType: "variableRef",
            variableId: varRefTmpl.variableId,
            ports: catalogEntry
              ? buildVariableRefPorts(catalogEntry, behaviorDico.controls.value)
              : [createOutputPort("string")],
          } satisfies import("../../../util/src/blueprint/types.js").VariableRefNode;
        }
        const variableTemplate = template as {
          valueType?: string;
          objectSource?: string;
        };
        if (
          variableTemplate.valueType === "node" &&
          variableTemplate.objectSource === "barLoaderDisks"
        ) {
          const ownerProps = (
            node.kind === "component" ? node.props : undefined
          ) as
            | {
                unit?: "kg" | "lbs";
                optionalDisks?: { kg50?: boolean; lbs100?: boolean };
              }
            | undefined;
          const unit = ownerProps?.unit ?? "kg";
          const weights = BAR_LOADER_DISKS_BY_UNIT[unit]
            .filter((disk) => {
              if (!disk.optional) return true;
              return unit === "kg"
                ? Boolean(ownerProps?.optionalDisks?.kg50)
                : Boolean(ownerProps?.optionalDisks?.lbs100);
            })
            .map((disk) => disk.weight);
          const defaultWeight = weights[0] ?? (unit === "kg" ? 25 : 55);
          return {
            id,
            x,
            y,
            type: "variable",
            valueType: "node",
            objectSource: "barLoaderDisks",
            nodeWeights: weights,
            nodeUnit: unit,
            ports: [
              {
                id: "output",
                label: "",
                direction: "output",
                valueType: "node",
                value: {
                  component: "barLoader",
                  weight: defaultWeight,
                  part: "disk",
                },
              },
            ],
          };
        }
        if (template.valueType === "object") {
          const objectSource = variableTemplate.objectSource;
          if (objectSource === LAYOUT_LIST_COMPARATOR_OBJECT_SOURCE) {
            return {
              id,
              x,
              y,
              type: "variable",
              valueType: "object",
              objectSource,
              ports: [
                {
                  id: "outputA",
                  label: behaviorDico.actions.comparatorA,
                  direction: "output",
                  valueType: "object",
                },
                {
                  id: "outputB",
                  label: behaviorDico.actions.comparatorB,
                  direction: "output",
                  valueType: "object",
                },
              ],
            };
          }
          return {
            id,
            x,
            y,
            type: "variable",
            valueType: "object",
            objectSource,
            ports: [
              {
                ...createOutputPort("object"),
                ...(objectSource?.startsWith("@objectBase:")
                  ? {
                      label: (() => {
                        const entry = sceneCatalog?.objectTypes?.find((entry) =>
                          objectSource.startsWith(
                            "@objectBase:" + entry.id + "/",
                          ),
                        );
                        const ext =
                          entry &&
                          sceneObjectExtensions(entry).find(
                            (e) =>
                              objectBaseSource(entry.id, e.portId) ===
                              objectSource,
                          );
                        const raw =
                          ext && sceneCatalog
                            ? sceneObjectTypeName(sceneCatalog, ext.typeId)
                            : "";
                        return (
                          (
                            dico().licensedPortal.layoutEditor
                              .dataSources as Record<string, string>
                          )[raw] ?? raw
                        );
                      })(),
                      objectTypeId: sceneCatalog?.objectTypes
                        ?.flatMap((entry) =>
                          sceneObjectExtensions(entry).map((extension) => ({
                            ...extension,
                            source: objectBaseSource(
                              entry.id,
                              extension.portId,
                            ),
                          })),
                        )
                        .find((extension) => extension.source === objectSource)
                        ?.typeId,
                    }
                  : {}),
              },
            ],
          };
        }
        // Scalar source-bound variable: reads a specific InjectedData primitive field.
        if (template.objectSource) {
          const resolvedType =
            getScalarSourceValueType(template.objectSource) ??
            template.valueType;
          let listItemValueType: Exclude<VariableValueType, "list"> | undefined;
          if (resolvedType === "list") {
            if ((getPathSchema(template.objectSource)?.length ?? 0) > 0) {
              listItemValueType = "object";
            } else {
              // Primitive array source (e.g. attemptTimers: string[],
              // attemptTimerSeconds: number[]): infer item type from mock data
              // so downstream `get(list, index)` outputs the correct value type.
              const mockData = getMockLayoutData();
              const arr =
                mockData[template.objectSource as keyof typeof mockData];
              if (Array.isArray(arr) && arr.length > 0) {
                const inferred = inferVariableValueTypeFromValue(arr[0]);
                listItemValueType = (
                  inferred === "list" ? "string" : inferred
                ) as Exclude<VariableValueType, "list">;
              } else {
                listItemValueType = "string";
              }
            }
          }
          return {
            id,
            x,
            y,
            type: "variable",
            valueType: resolvedType,
            objectSource: template.objectSource,
            listItemValueType,
            ports: [createOutputPort(resolvedType)],
          };
        }
        return {
          id,
          x,
          y,
          type: "variable",
          valueType: template.valueType,
          listItemValueType:
            template.valueType === "list" ? "string" : undefined,
          ports:
            variableTemplate.valueType === "layoutListTarget"
              ? [
                  {
                    ...createOutputPort("layoutListTarget"),
                    value: layoutListTargetOptions[0]
                      ? ({
                          kind: "layoutList",
                          nodeId: layoutListTargetOptions[0].nodeId,
                        } satisfies LayoutListTargetValue)
                      : undefined,
                  },
                ]
              : buildVariableLiteralPorts(template.valueType),
        };
      }

      if (template.type === "event") {
        if (template.kind === "onLoad") {
          return {
            id,
            x,
            y,
            type: "event",
            kind: "onLoad",
            ports: [createOutputPort("flow")],
          };
        }
        if (template.kind === "onListEnter" || template.kind === "onListExit") {
          return {
            id,
            x,
            y,
            type: "event",
            kind: template.kind,
            ports: [createOutputPort("flow")],
          };
        }
        if (
          template.kind === "onListFilterExit" ||
          template.kind === "onListFilterEnter"
        ) {
          return {
            id,
            x,
            y,
            type: "event",
            kind: template.kind,
            ports: [
              {
                ...createInputPort("target", "layoutListTarget"),
                label: dico().behavior.actions.setTarget,
              },
              {
                ...createOutputPort("flow"),
                label: dico().behavior.events.output,
              },
            ],
          };
        }
        if (template.kind === "onCustomSceneEvent") {
          return {
            id,
            x,
            y,
            type: "event",
            kind: "onCustomSceneEvent",
            customEventId: (template as CustomSceneEventNodeTemplate).eventId,
            ports: [createOutputPort("flow")],
          };
        }
        // onChange: data-source driven
        const objectSource =
          template.kind === "onChange" ? template.objectSource : undefined;
        const defaultFieldPath = objectSource
          ? getFlattenedPathOptions(getPathSchema(objectSource as DataSourceId))
              .length > 0
            ? ""
            : undefined
          : undefined;
        return {
          id,
          x,
          y,
          type: "event",
          kind: template.kind,
          objectSource,
          objectFieldPath: defaultFieldPath,
          triggerOnInit: false,
          ports: [createOutputPort("flow")],
        };
      }

      if (template.type === "control") {
        if (template.controlType === "wait") {
          return {
            id,
            x,
            y,
            type: "control",
            controlType: "wait",
            dataValueType: "flow",
            ports: [
              {
                ...createInputPort("value", "flow"),
                label: behaviorDico.controls.value,
              },
              {
                ...createInputPort("delayMs", "number"),
                label: behaviorDico.controls.delayMs,
                value: 1000,
              },
              {
                ...createOutputPort("flow"),
                id: "out",
                label: behaviorDico.actions.flowOut,
              },
            ],
          };
        }
        if (template.controlType === "repeat") {
          return {
            id,
            x,
            y,
            type: "control",
            controlType: "repeat",
            dataValueType: "flow",
            ports: [
              {
                ...createInputPort("value", "flow"),
                label: behaviorDico.controls.value,
              },
              {
                ...createInputPort("count", "number"),
                label: behaviorDico.controls.count,
                value: 1,
              },
              {
                ...createOutputPort("flow"),
                id: "body",
                label: behaviorDico.controls.body,
              },
              {
                ...createOutputPort("number"),
                id: "loopIndex",
                label: behaviorDico.controls.loopIndex,
              },
              {
                ...createOutputPort("flow"),
                id: "done",
                label: behaviorDico.controls.done,
              },
            ],
          };
        }
        if (template.controlType === "mergeFlows") {
          return {
            id,
            x,
            y,
            type: "control",
            controlType: "mergeFlows",
            dataValueType: "flow",
            ports: [
              {
                ...createInputPort("in1", "flow"),
                label: behaviorDico.controls.flowIn1,
              },
              {
                ...createInputPort("in2", "flow"),
                label: behaviorDico.controls.flowIn2,
              },
              {
                ...createOutputPort("flow"),
                id: "out",
                label: behaviorDico.actions.flowOut,
              },
            ],
          };
        }
        if (template.controlType === "eventOutput") {
          return {
            id,
            x,
            y,
            type: "control",
            controlType: "eventOutput",
            dataValueType: "flow",
            ports: [
              {
                ...createInputPort("in", "flow"),
                label: behaviorDico.actions.flowIn,
              },
            ],
          };
        }
        if (template.controlType === "functionBegin") {
          return {
            id,
            x,
            y,
            type: "control",
            controlType: "functionBegin",
            dataValueType: "flow",
            ports: [
              {
                ...createOutputPort("flow"),
                id: "out",
                label: behaviorDico.actions.flowOut,
              },
            ],
          };
        }
        if (template.controlType === "functionEnd") {
          return {
            id,
            x,
            y,
            type: "control",
            controlType: "functionEnd",
            dataValueType: "flow",
            ports: [
              {
                ...createInputPort("in", "flow"),
                label: behaviorDico.actions.flowIn,
              },
            ],
          };
        }
        if (template.controlType === "variableBegin") {
          return {
            id,
            x,
            y,
            type: "control",
            controlType: "variableBegin",
            dataValueType: "string",
            ports: [],
          };
        }
        if (template.controlType === "variableEnd") {
          return {
            id,
            x,
            y,
            type: "control",
            controlType: "variableEnd",
            dataValueType: "string",
            ports: [
              {
                ...createInputPort("value", "string"),
                label: behaviorDico.controls.value,
              },
            ],
          };
        }
        return {
          id,
          x,
          y,
          type: "control",
          controlType: "ifElse",
          dataValueType: "flow",
          ports: [
            {
              ...createInputPort("value", "flow"),
              label: behaviorDico.controls.value,
            },
            { ...createInputPort("condition", "boolean"), label: "" },
            {
              ...createOutputPort("flow"),
              id: "true",
              label: behaviorDico.controls.true,
            },
            {
              ...createOutputPort("flow"),
              id: "false",
              label: behaviorDico.controls.false,
            },
          ],
        };
      }

      if (template.type === "action" && template.kind === "set") {
        const isBarLoaderNode =
          node.kind === "component" && node.component === "barLoader";
        return {
          id,
          x,
          y,
          type: "action",
          kind: "set",
          key: template.key,
          expandedPorts: false,
          ports: [
            { ...createInputPort("in", "flow"), label: "" },
            ...buildActionValuePorts(template.key, false),
            ...(isBarLoaderNode &&
            template.key !== "totalWeight" &&
            template.key !== "barWeight" &&
            template.key !== "setBinding"
              ? [
                  {
                    ...createInputPort("target", "node"),
                    label: behaviorDico.actions.setTarget,
                  },
                ]
              : []),
            {
              id: "out",
              label: "",
              direction: "output" as const,
              valueType: "flow",
            },
          ],
        };
      }

      if (template.type === "action" && template.kind === "animBegin") {
        const anim = behaviorDico.animation;
        return {
          id,
          x,
          y,
          type: "action",
          kind: "animBegin",
          ports: [
            {
              id: "out",
              label: behaviorDico.actions.flowOut,
              direction: "output" as const,
              valueType: "flow",
            },
            {
              ...createInputPort("durationMs", "number"),
              label: anim.durationMs,
              value: 1000,
            },
            {
              ...createInputPort("delayMs", "number"),
              label: anim.delayMs,
              value: 0,
            },
            {
              ...createInputPort("iterationCount", "number"),
              label: anim.iterationCount,
              value: 1,
            },
            {
              ...createInputPort("direction", "animationDirection"),
              label: anim.direction,
              value: "normal",
            },
            {
              ...createInputPort("fillMode", "animationFill"),
              label: anim.fillMode,
              value: "forwards",
            },
          ],
        };
      }

      if (template.type === "action" && template.kind === "createRowVariant") {
        const list = behaviorDico.layoutList;
        return {
          id,
          x,
          y,
          type: "action",
          kind: "createRowVariant",
          ports: [
            {
              ...createInputPort("in", "flow"),
              label: behaviorDico.actions.flowIn,
            },
            {
              ...createInputPort("name", "string"),
              label: list.createRowVariant.name,
              value: "",
            },
            {
              ...createInputPort("condition", "boolean"),
              label: list.createRowVariant.condition,
              value: false,
            },
            {
              ...createInputPort("order", "number"),
              label: list.createRowVariant.order,
              value: 0,
            },
          ],
        };
      }

      if (template.type === "action" && template.kind === "animKeyframe") {
        const anim = behaviorDico.animation;
        const layoutDefaults = getAnimKeyframeDefaultsFromLayoutNode(node);
        const siblingKeyframe = renderedNodes.find(
          (entry) => entry.type === "action" && entry.kind === "animKeyframe",
        );
        const properties = siblingKeyframe
          ? getAnimKeyframePropertyIds(siblingKeyframe)
          : [];
        const includeEasing = siblingKeyframe
          ? keyframeHasEasingPort(siblingKeyframe)
          : true;
        const propertyValues = Object.fromEntries(
          properties.map((prop) => [prop, layoutDefaults[prop]]),
        ) as Partial<Record<AnimatableProperty, number>>;
        return {
          id,
          x,
          y,
          type: "action",
          kind: "animKeyframe",
          ports: buildAnimKeyframePorts(
            {
              flowIn: behaviorDico.actions.flowIn,
              flowOut: behaviorDico.actions.flowOut,
              offset: anim.offset,
              offsetOut: anim.offsetOut,
              easing: anim.easing,
              easingOut: anim.easingOut,
              mirrorSuffix: anim.mirrorSuffix,
              props: anim.props,
            },
            {
              offset: 0,
              easing: "ease",
              includeEasing,
              properties,
              propertyValues,
            },
          ),
        };
      }

      if (template.type === "action" && template.kind === "animEnd") {
        return {
          id,
          x,
          y,
          type: "action",
          kind: "animEnd",
          ports: [
            {
              ...createInputPort("in", "flow"),
              label: behaviorDico.actions.flowIn,
            },
          ],
        };
      }

      if (template.type === "action" && template.kind === "animPlay") {
        const anim = behaviorDico.animation;
        const templatePorts = (
          template as { ports?: Array<{ id: string; value?: unknown }> }
        ).ports;
        const presetAnimationName = String(
          templatePorts?.find((port) => port.id === "name")?.value ?? "",
        );
        const basePorts: Port[] = [
          {
            ...createInputPort("in", "flow"),
            label: behaviorDico.actions.flowIn,
          },
          {
            id: "out",
            label: behaviorDico.actions.flowOut,
            direction: "output" as const,
            valueType: "flow",
          },
          {
            ...createInputPort("name", "string"),
            label: anim.name,
            value: presetAnimationName,
          },
          {
            ...createInputPort("durationMs", "number"),
            label: anim.durationMs,
          },
          {
            ...createInputPort("delayMs", "number"),
            label: anim.delayMs,
          },
          {
            ...createInputPort("iterationCount", "number"),
            label: anim.iterationCount,
          },
          {
            ...createInputPort("direction", "animationDirection"),
            label: anim.direction,
            value: "normal",
          },
          {
            ...createInputPort("fillMode", "animationFill"),
            label: anim.fillMode,
            value: "forwards",
          },
        ];
        return {
          id,
          x,
          y,
          type: "action",
          kind: "animPlay",
          ports: presetAnimationName
            ? applyAnimPlayDefaultsToPorts(
                basePorts,
                presetAnimationName,
                sceneAnimationsByName,
              )
            : basePorts,
        };
      }

      if (template.type === "action" && template.kind === "call") {
        const funcTmpl =
          template as import("../../../util/src/blueprint/types.js").FunctionCallNodeTemplate;
        const entry = sceneCatalog?.functions.find(
          (item) => item.id === funcTmpl.functionId,
        );
        const callPorts = entry
          ? buildFunctionCallPorts(entry, {
              flowIn: behaviorDico.actions.flowIn,
              flowOut: behaviorDico.actions.flowOut,
            })
          : [
              {
                ...createInputPort("in", "flow"),
                label: behaviorDico.actions.flowIn,
              },
              {
                id: "out",
                label: behaviorDico.actions.flowOut,
                direction: "output" as const,
                valueType: "flow",
              },
            ];
        return {
          id,
          x,
          y,
          type: "action" as const,
          kind: "call" as const,
          functionId: funcTmpl.functionId,
          ports: callPorts,
        };
      }

      if (template.type === "action" && template.kind === "setVariable") {
        const setVarTmpl =
          template as import("../../../util/src/blueprint/types.js").SetVariableNodeTemplate;
        const entry = sceneCatalog?.variables.find(
          (item) => item.id === setVarTmpl.variableId,
        );
        const setVarPorts = entry
          ? buildSetVariablePorts(entry, {
              flowIn: behaviorDico.actions.flowIn,
              flowOut: behaviorDico.actions.flowOut,
              value: behaviorDico.actions.setVariableValue,
            })
          : [
              {
                ...createInputPort("in", "flow"),
                label: behaviorDico.actions.flowIn,
              },
              {
                ...createInputPort("value", "string"),
                label: behaviorDico.actions.setVariableValue,
              },
              {
                id: "out",
                label: behaviorDico.actions.flowOut,
                direction: "output" as const,
                valueType: "flow",
              },
            ];
        return {
          id,
          x,
          y,
          type: "action" as const,
          kind: "setVariable" as const,
          variableId: setVarTmpl.variableId,
          ports: setVarPorts,
        };
      }

      const operatorTemplate = template as Extract<
        BehaviorDraggedNodeTemplate,
        { type: "operator" }
      >;
      const outputType = operatorTemplate.outputValueType;
      const ports =
        operatorTemplate.mode === "ternary"
          ? [
              createInputPort("input1", operatorTemplate.inputValueTypes[0]!),
              createInputPort(
                "input2",
                operatorTemplate.inputValueTypes[1] ??
                  operatorTemplate.inputValueTypes[0]!,
              ),
              createInputPort(
                "input3",
                operatorTemplate.inputValueTypes[2] ??
                  operatorTemplate.inputValueTypes[0]!,
              ),
              createOutputPort(outputType),
            ]
          : operatorTemplate.mode === "binary"
            ? [
                createInputPort("input1", operatorTemplate.inputValueTypes[0]!),
                createInputPort(
                  "input2",
                  operatorTemplate.inputValueTypes[1] ??
                    operatorTemplate.inputValueTypes[0]!,
                ),
                createOutputPort(outputType),
              ]
            : [
                createInputPort("input1", operatorTemplate.inputValueTypes[0]!),
                createOutputPort(outputType),
              ];

      const opKey = operatorTemplate.operator as Operator;
      const isDynamicListOperator =
        opKey === "push" ||
        opKey === "get" ||
        opKey === "find" ||
        opKey === "findByPath" ||
        opKey === "findByCondition" ||
        opKey === "filter" ||
        opKey === "sort" ||
        opKey === "take" ||
        opKey === "currentItem" ||
        opKey === "comparator";
      const listItemValueType: Exclude<VariableValueType, "list"> = "string";

      let baseDynamicPorts = ports;
      if (opKey === "dateDifference") {
        baseDynamicPorts = dateDifferencePorts(
          behaviorDico.operators.dateDifferencePorts,
        );
      }
      if (opKey === "comparator") {
        // `comparator` is a unary node with TWO output ports (`outputA` and
        // `outputB`) instead of a single `output`. Both expose the current
        // pair of items being compared by `sort`. We rebuild the ports here
        // (rather than going through `createOutputPort` which always emits
        // `id: "output"`).
        baseDynamicPorts = [
          createInputPort("input1", "list"),
          {
            id: "outputA",
            label: "A",
            direction: "output" as const,
            valueType: listItemValueType,
            value: undefined,
          },
          {
            id: "outputB",
            label: "B",
            direction: "output" as const,
            valueType: listItemValueType,
            value: undefined,
          },
        ];
      }

      // `find` / `findByPath` / `findByCondition` expose a SECOND output port
      // `outputIndex` (always `number`, `-1` when no match), in addition to
      // `output` (the matched item, dynamically typed by the list).
      // Note: existing graphs saved before this change won't have the port
      // declared on disk; the runtime still works because it dispatches on
      // `portId` directly, but the UI will only show `outputIndex` on nodes
      // created via the palette after this change.
      if (
        opKey === "find" ||
        opKey === "findByPath" ||
        opKey === "findByCondition"
      ) {
        baseDynamicPorts = [
          ...baseDynamicPorts,
          {
            id: "outputIndex",
            label: behaviorDico.actions.findIndexOutput,
            direction: "output" as const,
            valueType: "number",
            // Output ports do not carry a typed default value; the resolver
            // returns `-1` at runtime when no match is found.
            value: undefined,
          },
        ];
      }

      const normalizedPorts = isDynamicListOperator
        ? baseDynamicPorts.map((port) => {
            if (port.id === "input2" && opKey === "push") {
              return { ...port, valueType: listItemValueType, value: "" };
            }
            if (
              port.id === "output" &&
              (opKey === "get" ||
                opKey === "find" ||
                opKey === "findByPath" ||
                opKey === "findByCondition" ||
                opKey === "currentItem")
            ) {
              return {
                ...port,
                valueType: listItemValueType,
                value: undefined,
              };
            }
            if (port.id === "input2" && opKey === "find") {
              return {
                ...port,
                valueType: listItemValueType,
                value: undefined,
              };
            }
            return port;
          })
        : baseDynamicPorts;
      const nodeBase = {
        id,
        x,
        y,
        type: "operator" as const,
        operator: opKey,
        mode: operatorTemplate.mode,
        objectSource:
          opKey === "get" && operatorTemplate.inputValueTypes[0] === "object"
            ? ((customObjectPathSchema
                ? Object.keys(customObjectPathSchema).find(
                    (key) => (customObjectPathSchema[key]?.length ?? 0) > 0,
                  )
                : (
                    Object.keys(DATA_SOURCE_PATHS) as Array<
                      keyof typeof DATA_SOURCE_PATHS
                    >
                  ).find(sourceHasPaths)) ?? undefined)
            : undefined,
        objectFieldPathBase: undefined as string | undefined,
        objectFieldPath: undefined as string | undefined,
        listItemValueType:
          opKey === "groupBy" ||
          opKey === "aggregateGroups" ||
          opKey === "objectEntries"
            ? ("object" as const)
            : isDynamicListOperator
              ? listItemValueType
              : (undefined as Exclude<VariableValueType, "list"> | undefined),
        ports: normalizedPorts,
      };
      if (opKey === "objectEntries" || opKey === "currentEntry")
        nodeBase.objectSource = "@objectEntries";
      if (opKey === "mapItems" && operatorTemplate.inputValueTypes[1] !== "any")
        nodeBase.listItemValueType = (
          operatorTemplate.inputValueTypes[1] === "list"
            ? "object"
            : operatorTemplate.inputValueTypes[1]
        ) as Exclude<VariableValueType, "list">;
      if (opKey === "get" && operatorTemplate.inputValueTypes[0] === "object") {
        const options = nodeBase.objectSource
          ? getFlattenedPathOptions(
              getPathSchemaAtPath(
                getPathSchema(nodeBase.objectSource),
                nodeBase.objectFieldPathBase,
              ),
              { includeChildren: false },
            )
          : [];
        const defaultFieldPath = options[0]?.value;
        const outputValueType = getObjectFieldValueType(
          nodeBase.objectSource,
          defaultFieldPath,
          nodeBase.objectFieldPathBase,
        );
        nodeBase.objectFieldPath = defaultFieldPath;
        nodeBase.ports = nodeBase.ports.map((port) =>
          port.id === "output"
            ? { ...port, valueType: outputValueType, value: undefined }
            : port,
        );
      }
      if (
        operatorTemplate.mode === "unary" &&
        (opKey === "getDay" || opKey === "getMonth")
      ) {
        return {
          ...nodeBase,
          datePartTextFormat: "numeric" satisfies DatePartTextFormat,
        };
      }
      return nodeBase;
    };

    const findCompatibleInputPort = useCallback(
      (
        targetNode: BehaviorNode,
        sourcePort: Port,
        preferredPortId?: string,
      ) => {
        const compatiblePorts = targetNode.ports.filter(
          (port) =>
            port.direction === "input" &&
            areBehaviorPortTypesCompatible(
              sourcePort.valueType,
              port.valueType,
            ),
        );
        if (compatiblePorts.length === 0) {
          return undefined;
        }
        return (
          compatiblePorts.find((port) => port.id === preferredPortId) ??
          compatiblePorts[0]
        );
      },
      [],
    );

    const buildConnectedGraphSnapshot = (
      nodeList: BehaviorNode[],
      rawConnections: BehaviorConnection[],
      newConnection: BehaviorConnection,
    ): BehaviorGraphSnapshot => {
      const flowLabels = {
        flowIn: behaviorDico.actions.flowIn,
        flowOut: behaviorDico.actions.flowOut,
      };
      const refLabels = { refOutput: behaviorDico.controls.value };
      let resolvedNodes = nodeList;
      if (sceneCatalog) {
        resolvedNodes = syncCallNodePortsInGraph(
          { nodes: nodeList, connections: [] },
          sceneCatalog.functions,
          flowLabels,
          { filterConnections: false },
        ).nodes;
        resolvedNodes = syncVariableRefNodePortsInGraph(
          { nodes: resolvedNodes, connections: [] },
          sceneCatalog.variables,
          refLabels,
          { filterConnections: false },
        ).nodes;
      }
      const listTypedNodes = applyListTypingFromConnection(
        resolvedNodes,
        newConnection,
      );
      const objectTypedNodes = applyObjectGetTypingFromConnection(
        listTypedNodes,
        newConnection,
      );
      const synchronizedNodes = customObjectPathSchema
        ? synchronizeBehaviorObjectPaths(
            { nodes: objectTypedNodes, connections: rawConnections },
            customObjectPathSchema,
          ).nodes
        : synchronizeListFindOperators(
            synchronizeObjectGetNodes(objectTypedNodes, rawConnections),
            rawConnections,
          );
      const nextConnections = rawConnections.filter((connection) => {
        const fromNode = synchronizedNodes.find(
          (node) => node.id === connection.fromNodeId,
        );
        const toNode = synchronizedNodes.find(
          (node) => node.id === connection.toNodeId,
        );
        const fromPort = fromNode?.ports.find(
          (port) => port.id === connection.fromPortId,
        );
        const toPort = toNode?.ports.find(
          (port) => port.id === connection.toPortId,
        );
        if (!fromPort || !toPort) return false;
        return areBehaviorPortTypesCompatible(
          fromPort.valueType,
          toPort.valueType,
        );
      });
      const snapshot = {
        nodes: synchronizedNodes,
        connections: nextConnections,
      };
      return sceneCatalog
        ? syncVariableRefNodePortsInGraph(
            syncCallNodePortsInGraph(
              snapshot,
              sceneCatalog.functions,
              flowLabels,
            ),
            sceneCatalog.variables,
            refLabels,
          )
        : snapshot;
    };

    const commitBehaviorGraphSnapshot = useCallback(
      (
        snapshot: BehaviorGraphSnapshot,
        connectionsChanged = true,
      ): BehaviorGraphSnapshot => {
        const flowLabels = {
          flowIn: behaviorDico.actions.flowIn,
          flowOut: behaviorDico.actions.flowOut,
        };
        const refLabels = { refOutput: behaviorDico.controls.value };
        const committed = sceneCatalog
          ? syncVariableRefNodePortsInGraph(
              syncCallNodePortsInGraph(
                snapshot,
                sceneCatalog.functions,
                flowLabels,
              ),
              sceneCatalog.variables,
              refLabels,
            )
          : snapshot;
        if (onBehaviorGraphCommit) {
          onBehaviorGraphCommit(committed);
          return committed;
        }
        updateNodes(() => committed.nodes);
        if (connectionsChanged) {
          onConnectionsChange?.(committed.connections);
        }
        return committed;
      },
      [
        behaviorDico.actions.flowIn,
        behaviorDico.actions.flowOut,
        behaviorDico.controls.value,
        onBehaviorGraphCommit,
        onConnectionsChange,
        sceneCatalog,
        updateNodes,
      ],
    );

    const insertNodeFromTemplate = (
      template: BehaviorDraggedNodeTemplate,
      anchor: BehaviorPortPosition,
      options?: {
        sourceNodeId: string;
        sourcePortId: string;
        sourcePort: Port;
        preferredPortId?: string;
      },
    ) => {
      if (
        scope === "scene" &&
        template.type === "action" &&
        (template.kind === "animBegin" || template.kind === "animEnd") &&
        sceneAnimationGraphHasTerminalKind(
          { nodes: renderedNodes, connections },
          template.kind,
        )
      ) {
        return null;
      }
      if (
        scope === "triggerGraph" &&
        template.type === "control" &&
        template.controlType === "eventOutput" &&
        triggerGraphHasEventOutput({ nodes: renderedNodes })
      ) {
        return null;
      }
      if (
        scope === "functionGraph" &&
        template.type === "control" &&
        template.controlType === "functionBegin" &&
        functionGraphHasFunctionBegin({ nodes: renderedNodes })
      ) {
        return null;
      }
      if (
        scope === "functionGraph" &&
        template.type === "control" &&
        template.controlType === "functionEnd" &&
        functionGraphHasFunctionEnd({ nodes: renderedNodes })
      ) {
        return null;
      }
      if (
        scope === "variableGraph" &&
        template.type === "control" &&
        template.controlType === "variableBegin" &&
        variableGraphHasVariableBegin({ nodes: renderedNodes })
      ) {
        return null;
      }
      if (
        scope === "variableGraph" &&
        template.type === "control" &&
        template.controlType === "variableEnd" &&
        variableGraphHasVariableEnd({ nodes: renderedNodes })
      ) {
        return null;
      }
      if (
        scope === "triggerGraph" &&
        editingCustomEventId &&
        template.type === "event" &&
        template.kind === "onCustomSceneEvent" &&
        (template as CustomSceneEventNodeTemplate).eventId ===
          editingCustomEventId
      ) {
        return null;
      }

      const nextNode = buildNodeFromTemplate(
        template,
        Math.max(0, anchor.x - 80),
        Math.max(0, anchor.y - 30),
      );
      if (!options) {
        commitBehaviorGraphSnapshot(
          {
            nodes: [...renderedNodes, nextNode],
            connections,
          },
          false,
        );
        setSelectedNodeIds([nextNode.id]);
        return nextNode;
      }

      const compatibleInput = findCompatibleInputPort(
        nextNode,
        options.sourcePort,
        options.preferredPortId,
      );
      if (!compatibleInput) {
        commitBehaviorGraphSnapshot(
          {
            nodes: [...renderedNodes, nextNode],
            connections,
          },
          false,
        );
        setSelectedNodeIds([nextNode.id]);
        return nextNode;
      }

      const newConnection: BehaviorConnection = {
        id: buildConnectionId(
          options.sourceNodeId,
          options.sourcePortId,
          nextNode.id,
          compatibleInput.id,
        ),
        fromNodeId: options.sourceNodeId,
        fromPortId: options.sourcePortId,
        toNodeId: nextNode.id,
        toPortId: compatibleInput.id,
      };
      const snapshot = buildConnectedGraphSnapshot(
        [...renderedNodes, nextNode],
        [...connections, newConnection],
        newConnection,
      );
      commitBehaviorGraphSnapshot(snapshot);
      setSelectedNodeIds([nextNode.id]);
      return nextNode;
    };

    const getDraggedTemplate = (
      event: DragEvent<HTMLElement>,
    ): BehaviorDraggedNodeTemplate | null => {
      const rawPayload =
        event.dataTransfer.getData(BEHAVIOR_NODE_DRAG_MIME) ||
        event.dataTransfer.getData("text/plain");
      if (!rawPayload) {
        return null;
      }
      try {
        return JSON.parse(rawPayload) as BehaviorDraggedNodeTemplate;
      } catch {
        return null;
      }
    };

    const handleCanvasDrop = (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (readOnly) return;
      const template = getDraggedTemplate(event);
      if (!template) {
        return;
      }

      const pointer = getWorldPointerPosition(event.clientX, event.clientY);
      setPortConnectionMenu(null);
      insertNodeFromTemplate(template, pointer);
    };

    const handleCanvasWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
      const targetElement = event.target as HTMLElement | null;
      const isMenuScrollTarget =
        !!targetElement?.closest?.('[role="listbox"]') ||
        !!targetElement?.closest?.('[role="option"]') ||
        !!targetElement?.closest?.(".MuiMenu-paper") ||
        !!targetElement?.closest?.(".MuiPopover-paper") ||
        !!targetElement?.closest?.("[data-port-connection-picker]");
      event.stopPropagation();
      if (isMenuScrollTarget) {
        return;
      }
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      const relativeX = event.clientX - rect.left;
      const relativeY = event.clientY - rect.top;
      const isZoomGesture = event.ctrlKey || event.metaKey;
      if (!isZoomGesture) {
        updateViewport({
          offset: {
            x: viewportOffset.x - event.deltaX,
            y: viewportOffset.y - event.deltaY,
          },
          zoom: viewportZoom,
        });
        return;
      }

      const zoomFactor = event.deltaY < 0 ? 1.1 : 0.9;
      const nextZoom = Math.min(2.5, Math.max(0.4, viewportZoom * zoomFactor));
      const worldX = (relativeX - viewportOffset.x) / viewportZoom;
      const worldY = (relativeY - viewportOffset.y) / viewportZoom;
      updateViewport({
        offset: {
          x: relativeX - worldX * nextZoom,
          y: relativeY - worldY * nextZoom,
        },
        zoom: nextZoom,
      });
    };

    const handleConnectionClick = (
      event: MouseEvent<SVGPathElement>,
      connectionId: string,
    ) => {
      if (!event.ctrlKey && !event.metaKey) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const rawConnections = connections.filter(
        (connection) => connection.id !== connectionId,
      );
      const synchronizedNodes = synchronizeListFindOperators(
        synchronizeObjectGetNodes(renderedNodes, rawConnections),
        rawConnections,
      );
      const nextConnections = rawConnections.filter((connection) => {
        const fromNode = synchronizedNodes.find(
          (node) => node.id === connection.fromNodeId,
        );
        const toNode = synchronizedNodes.find(
          (node) => node.id === connection.toNodeId,
        );
        const fromPort = fromNode?.ports.find(
          (port) => port.id === connection.fromPortId,
        );
        const toPort = toNode?.ports.find(
          (port) => port.id === connection.toPortId,
        );
        if (!fromPort || !toPort) return false;
        return areBehaviorPortTypesCompatible(
          fromPort.valueType,
          toPort.valueType,
        );
      });
      if (onBehaviorGraphCommit) {
        onBehaviorGraphCommit({
          nodes: synchronizedNodes,
          connections: nextConnections,
        });
      } else {
        updateNodes(() => synchronizedNodes);
        onConnectionsChange?.(nextConnections);
      }
    };

    const handleObjectPortsChange = (
      nodeId: string,
      ports: Port[],
      renamed: Record<string, string> = {},
    ) => {
      if (!editingObjectTypeId || !sceneCatalog || readOnly) return;
      onBehaviorGraphCommit?.(
        updateSceneObjectDefinitionPorts(
          { nodes: renderedNodes, connections },
          editingObjectTypeId,
          nodeId,
          ports,
          renamed,
        ),
      );
    };

    const handlePortValueChange = (
      nodeId: string,
      portId: string,
      value:
        | string
        | number
        | boolean
        | ListValue
        | NodePortValue
        | LayoutListTargetValue
        | LocationValue
        | TransformValue
        | BorderConfig
        | BorderRadiusConfig
        | SpacingConfig,
    ) => {
      if (portId === "name") {
        const nextName = String(value ?? "");
        updateNodes((current) =>
          current.map((node) => {
            if (node.id !== nodeId) return node;

            // For animPlay, selecting a name loads scene animation defaults.
            if (node.type === "action" && node.kind === "animPlay") {
              return {
                ...node,
                ports: applyAnimPlayDefaultsToPorts(
                  node.ports,
                  nextName,
                  sceneAnimationsByName,
                ),
              };
            }

            return {
              ...node,
              ports: node.ports.map((port) =>
                port.id === portId ? { ...port, value: nextName } : port,
              ),
            };
          }),
        );
        return;
      }

      updateNodes((current) =>
        current.map((node) =>
          node.id === nodeId
            ? {
                ...node,
                ports: node.ports.map((port) =>
                  port.id === portId ? { ...port, value } : port,
                ),
              }
            : node,
        ),
      );
    };

    const getCompositePortNumber = (value: unknown): number => {
      if (typeof value === "number" && Number.isFinite(value)) return value;
      const next = Number(value);
      return Number.isFinite(next) ? next : 0;
    };

    const buildVariableLiteralPorts = useCallback(
      (
        valueType: VariableValueType,
        expandedPorts = false,
        existingPorts?: Port[],
      ): Port[] => {
        const valuePort = existingPorts?.find((port) => port.id === "output");
        const locationValue =
          valuePort?.value && typeof valuePort.value === "object"
            ? (valuePort.value as Partial<LocationValue>)
            : undefined;
        const transformValue =
          valuePort?.value && typeof valuePort.value === "object"
            ? (valuePort.value as Partial<TransformValue>)
            : undefined;

        if (valueType === "location") {
          const x = getCompositePortNumber(
            locationValue?.x ??
              existingPorts?.find((port) => port.id === "output.x")?.value,
          );
          const y = getCompositePortNumber(
            locationValue?.y ??
              existingPorts?.find((port) => port.id === "output.y")?.value,
          );
          const z = getCompositePortNumber(
            locationValue?.z ??
              existingPorts?.find((port) => port.id === "output.z")?.value,
          );
          if (expandedPorts) {
            return [
              {
                id: "output.x",
                label: behaviorDico.actions.locationX,
                direction: "output",
                valueType: "number",
                value: x,
              },
              {
                id: "output.y",
                label: behaviorDico.actions.locationY,
                direction: "output",
                valueType: "number",
                value: y,
              },
              {
                id: "output.z",
                label: behaviorDico.actions.locationZ,
                direction: "output",
                valueType: "number",
                value: z,
              },
            ];
          }
          return [
            {
              id: "output",
              label: "",
              direction: "output",
              valueType: "location",
              value: { x, y, z } as LocationValue,
            },
          ];
        }

        if (valueType === "transform") {
          const width = getCompositePortNumber(
            transformValue?.width ??
              existingPorts?.find((port) => port.id === "output.width")?.value,
          );
          const height = getCompositePortNumber(
            transformValue?.height ??
              existingPorts?.find((port) => port.id === "output.height")?.value,
          );
          const rotation = getCompositePortNumber(
            transformValue?.rotation ??
              existingPorts?.find((port) => port.id === "output.rotation")
                ?.value,
          );
          if (expandedPorts) {
            return [
              {
                id: "output.width",
                label: behaviorDico.actions.transformWidth,
                direction: "output",
                valueType: "number",
                value: width,
              },
              {
                id: "output.height",
                label: behaviorDico.actions.transformHeight,
                direction: "output",
                valueType: "number",
                value: height,
              },
              {
                id: "output.rotation",
                label: behaviorDico.actions.transformRotation,
                direction: "output",
                valueType: "number",
                value: rotation,
              },
            ];
          }
          return [
            {
              id: "output",
              label: "",
              direction: "output",
              valueType: "transform",
              value: { width, height, rotation } as TransformValue,
            },
          ];
        }

        return [
          {
            id: "output",
            label: "",
            direction: "output",
            valueType,
            value:
              valueType === "color"
                ? "#00000000"
                : valueType === "gradient"
                  ? "linear-gradient(90deg, #000000 0%, #ffffff 100%)"
                  : valueType === "filter"
                    ? "none"
                    : valueType === "boxShadow"
                      ? "none"
                      : valueType === "textShadow"
                        ? "none"
                        : valueType === "fontFamily"
                          ? "Arial"
                          : valueType === "fontWeight"
                            ? "normal"
                            : valueType === "textAlign"
                              ? "left"
                              : valueType === "textTransform"
                                ? "none"
                                : valueType === "whiteSpace"
                                  ? "normal"
                                  : valueType === "border"
                                    ? ({
                                        border: {
                                          top: "1px solid #000000",
                                          right: "1px solid #000000",
                                          bottom: "1px solid #000000",
                                          left: "1px solid #000000",
                                        },
                                      } satisfies BorderConfig)
                                    : valueType === "borderRadius"
                                      ? ({
                                          radius: {
                                            topLeft: "0px",
                                            topRight: "0px",
                                            bottomRight: "0px",
                                            bottomLeft: "0px",
                                          },
                                        } satisfies BorderRadiusConfig)
                                      : valueType === "spacing"
                                        ? ({
                                            margin: { scope: "all", value: 0 },
                                            padding: { scope: "all", value: 0 },
                                          } satisfies SpacingConfig)
                                        : valueType === "list"
                                          ? ([] satisfies ListValue)
                                          : valueType === "node"
                                            ? undefined
                                            : valueType === "animationDirection"
                                              ? "normal"
                                              : valueType === "animationFill"
                                                ? "forwards"
                                                : valueType ===
                                                    "animationEasing"
                                                  ? "ease"
                                                  : undefined,
          },
        ];
      },
      [behaviorDico.actions],
    );

    const buildActionValuePorts = useCallback(
      (key: ActionSetKey, expandedPorts?: boolean): Port[] => {
        if (isTimerAction(key))
          return timerActionPorts(key, behaviorDico.timers);
        if (key === "customSubmitForm") return [];
        if (key === "customGroupService")
          return [
            {
              id: "service",
              label: behaviorDico.actions.customGroupServiceName,
              direction: "input" as const,
              valueType: "string" as const,
              value: "synchronise",
            },
            {
              id: "groupId",
              label: behaviorDico.actions.customOpenGroupId,
              direction: "input" as const,
              valueType: "string" as const,
              value: "",
            },
            {
              id: "fieldMap",
              label: behaviorDico.actions.customGroupServiceMap,
              direction: "input" as const,
              valueType: "object" as const,
              value: "{}",
            },
            {
              id: "format",
              label: behaviorDico.actions.customGroupServiceFormat,
              direction: "input" as const,
              valueType: "string" as const,
              value: "",
            },
            {
              id: "athleteId",
              label: behaviorDico.actions.customSetCurrentAthleteId,
              direction: "input" as const,
              valueType: "string" as const,
              value: "",
            },
            {
              id: "options",
              label: behaviorDico.actions.customGroupServiceOptions,
              direction: "input" as const,
              valueType: "object" as const,
              value: "{}",
            },
          ];
        if (key === "customIncrementAthleteCount") return [];
        if (key === "customRunAction")
          return [
            {
              id: "actionKey",
              label: behaviorDico.actions.customRunActionKey,
              direction: "input" as const,
              valueType: "string" as const,
              value: "",
            },
            {
              id: "contextAthleteId",
              label: behaviorDico.actions.customOpenModalAthleteId,
              direction: "input" as const,
              valueType: "string" as const,
            },
            {
              id: "contextColumnKey",
              label: behaviorDico.actions.customRunActionColumn,
              direction: "input" as const,
              valueType: "string" as const,
            },
          ];
        if (key === "customListSetField") {
          return [
            {
              id: "list",
              label: behaviorDico.actions.customListSetFieldList,
              direction: "input" as const,
              valueType: "list" as const,
              value: [],
            },
            {
              id: "searchKey",
              label: behaviorDico.actions.customListSetFieldSearchKey,
              direction: "input" as const,
              valueType: "string" as const,
              value: "id",
            },
            {
              id: "searchValue",
              label: behaviorDico.actions.customListSetFieldSearchValue,
              direction: "input" as const,
              valueType: "string" as const,
              value: "",
            },
            {
              id: "fieldKey",
              label: behaviorDico.actions.customListSetFieldFieldKey,
              direction: "input" as const,
              valueType: "string" as const,
              value: "",
            },
            {
              id: "nextValue",
              label: behaviorDico.actions.customListSetFieldNextValue,
              direction: "input" as const,
              valueType: "any" as const,
              value: "",
            },
          ];
        }
        if (key === "customListSetRows") {
          return [
            {
              id: "list",
              label: behaviorDico.actions.customListSetRowsList,
              direction: "input" as const,
              valueType: "list" as const,
              value: [],
            },
            {
              id: "mode",
              label: behaviorDico.actions.customListSetRowsMode,
              direction: "input" as const,
              valueType: "string" as const,
              value: "replaceCurrentGroup",
            },
            {
              id: "targetGroupId",
              label: behaviorDico.actions.customListSetRowsTargetGroupId,
              direction: "input" as const,
              valueType: "string" as const,
              value: "",
            },
            {
              id: "persistFields",
              label: behaviorDico.actions.customListSetRowsPersistFields,
              direction: "input" as const,
              valueType: "boolean" as const,
              value: true,
            },
            {
              id: "deleteMissing",
              label: behaviorDico.actions.customListSetRowsDeleteMissing,
              direction: "input" as const,
              valueType: "boolean" as const,
              value: true,
            },
          ];
        }
        if (key === "customListSetViewRows") {
          return [
            {
              id: "list",
              label: behaviorDico.actions.customListSetViewRowsList,
              direction: "input" as const,
              valueType: "list" as const,
              value: [],
            },
            {
              id: "includeNewRows",
              label: behaviorDico.actions.customListSetViewRowsIncludeNewRows,
              direction: "input" as const,
              valueType: "boolean" as const,
              value: false,
            },
          ];
        }
        if (key === "customGroupSetField") {
          return [
            {
              id: "groupId",
              label: behaviorDico.actions.customGroupSetFieldGroupId,
              direction: "input" as const,
              valueType: "string" as const,
              value: "",
            },
            {
              id: "fieldKey",
              label: behaviorDico.actions.customGroupSetFieldFieldKey,
              direction: "input" as const,
              valueType: "string" as const,
              value: "",
            },
            {
              id: "nextValue",
              label: behaviorDico.actions.customGroupSetFieldNextValue,
              direction: "input" as const,
              valueType: "any" as const,
              value: "",
            },
          ];
        }
        if (key === "customOpenGroup") {
          return [
            {
              id: "groupId",
              label: behaviorDico.actions.customOpenGroupId,
              direction: "input" as const,
              valueType: "string" as const,
              value: "",
            },
          ];
        }
        if (key === "customSetCurrentGroup") {
          return [
            {
              id: "groupId",
              label: behaviorDico.actions.customSetCurrentGroupId,
              direction: "input" as const,
              valueType: "string" as const,
              value: "",
            },
          ];
        }
        if (key === "customSetCurrentAthlete") {
          return [
            {
              id: "athleteId",
              label: behaviorDico.actions.customSetCurrentAthleteId,
              direction: "input" as const,
              valueType: "string" as const,
              value: "",
            },
          ];
        }
        if (key === "customOpenModal") {
          return [
            {
              id: "modalKey",
              label: behaviorDico.actions.customOpenModalKey,
              direction: "input" as const,
              valueType: "string" as const,
              value: "",
            },
            {
              id: "contextAthleteId",
              label: behaviorDico.actions.customOpenModalAthleteId,
              direction: "input" as const,
              valueType: "string" as const,
              value: "",
            },
            {
              id: "contextGroupId",
              label: behaviorDico.actions.customOpenModalGroupId,
              direction: "input" as const,
              valueType: "string" as const,
              value: "",
            },
          ];
        }
        if (key === "customCloseModal" || key === "customPrintModal") {
          return [
            {
              id: "modalKey",
              label: behaviorDico.actions.customCloseModalKey,
              direction: "input" as const,
              valueType: "string" as const,
              value: "",
            },
          ];
        }
        const isBarLoaderNode =
          node.kind === "component" && node.component === "barLoader";
        const valueType =
          isBarLoaderNode && key === "setBinding"
            ? "number"
            : ACTION_SET_KEY_VALUE_TYPE[key];
        if (expandedPorts && key === "location") {
          return [
            {
              id: "value.x",
              label: behaviorDico.actions.locationX,
              direction: "input" as const,
              valueType: "number" as const,
            },
            {
              id: "value.y",
              label: behaviorDico.actions.locationY,
              direction: "input" as const,
              valueType: "number" as const,
            },
            {
              id: "value.z",
              label: behaviorDico.actions.locationZ,
              direction: "input" as const,
              valueType: "number" as const,
            },
          ];
        }
        if (expandedPorts && key === "transform") {
          return [
            {
              id: "value.width",
              label: behaviorDico.actions.transformWidth,
              direction: "input" as const,
              valueType: "number" as const,
            },
            {
              id: "value.height",
              label: behaviorDico.actions.transformHeight,
              direction: "input" as const,
              valueType: "number" as const,
            },
            {
              id: "value.rotation",
              label: behaviorDico.actions.transformRotation,
              direction: "input" as const,
              valueType: "number" as const,
            },
          ];
        }
        const defaultValue =
          key === "location"
            ? ({
                x: Math.round(node.x),
                y: Math.round(node.y),
                z: Math.round(node.zIndex),
              } as LocationValue)
            : key === "transform"
              ? ({
                  width: Math.round(node.width),
                  height: Math.round(node.height),
                  rotation: Math.round(node.rotation ?? 0),
                } as TransformValue)
              : valueType === "border"
                ? ({
                    border: {
                      top: "1px solid #000000",
                      right: "1px solid #000000",
                      bottom: "1px solid #000000",
                      left: "1px solid #000000",
                    },
                  } satisfies BorderConfig)
                : valueType === "borderRadius"
                  ? ({
                      radius: {
                        topLeft: "0px",
                        topRight: "0px",
                        bottomRight: "0px",
                        bottomLeft: "0px",
                      },
                    } satisfies BorderRadiusConfig)
                  : valueType === "spacing"
                    ? ({
                        margin: { scope: "all", value: 0 },
                        padding: { scope: "all", value: 0 },
                      } satisfies SpacingConfig)
                    : valueType === "color"
                      ? "#00000000"
                      : valueType === "filter"
                        ? "none"
                        : valueType === "boxShadow"
                          ? "none"
                          : valueType === "textShadow"
                            ? "none"
                            : valueType === "fontFamily"
                              ? "Arial"
                              : valueType === "fontWeight"
                                ? "normal"
                                : valueType === "textAlign"
                                  ? "left"
                                  : valueType === "textTransform"
                                    ? "none"
                                    : valueType === "whiteSpace"
                                      ? "normal"
                                      : valueType === "boolean"
                                        ? true
                                        : valueType === "number"
                                          ? key === "totalWeight"
                                            ? 125
                                            : key === "barWeight"
                                              ? 25
                                              : 0
                                          : "";
        return [
          {
            id: "value",
            label: "",
            direction: "input" as const,
            valueType,
            value: defaultValue,
          },
        ];
      },
      [behaviorDico.actions, node],
    );

    const compatiblePortConnectionItems: Array<
      BehaviorTemplateCatalogItem & { targetPortId: string }
    > = portConnectionMenu
      ? portConnectionTemplateCatalog.flatMap((item) => {
          const previewNode = buildNodeFromTemplate(item.template, 0, 0);
          const compatibleInput = findCompatibleInputPort(
            previewNode,
            portConnectionMenu.sourcePort,
          );
          if (!compatibleInput) {
            return [];
          }
          return [{ ...item, targetPortId: compatibleInput.id }];
        })
      : [];

    const handleSelectPortConnectionTemplate = (
      item: PortConnectionPickerItem,
    ) => {
      if (!portConnectionMenu) {
        return;
      }
      if (!item.targetPortId) {
        return;
      }
      insertNodeFromTemplate(item.template, portConnectionMenu.anchor, {
        sourceNodeId: portConnectionMenu.sourceNodeId,
        sourcePortId: portConnectionMenu.sourcePortId,
        sourcePort: portConnectionMenu.sourcePort,
        preferredPortId: item.targetPortId,
      });
      setPortConnectionMenu(null);
    };

    const handleToggleExpandedPorts = useCallback(
      (nodeId: string) => {
        updateNodes((current) =>
          current.map((node) => {
            if (
              node.id !== nodeId ||
              node.type !== "action" ||
              node.kind !== "set"
            ) {
              return node;
            }
            if (node.key !== "location" && node.key !== "transform") {
              return node;
            }
            const nextExpanded = !node.expandedPorts;
            const inPort = node.ports.find((p) => p.id === "in");
            const outPort = node.ports.find((p) => p.id === "out");
            const targetPort = node.ports.find((p) => p.id === "target");
            if (!inPort || !outPort) return node;
            return {
              ...node,
              expandedPorts: nextExpanded,
              ports: [
                inPort,
                ...buildActionValuePorts(node.key, nextExpanded),
                ...(targetPort ? [targetPort] : []),
                outPort,
              ],
            };
          }),
        );
      },
      [buildActionValuePorts, updateNodes],
    );

    const handleToggleExpandedVariablePorts = useCallback(
      (nodeId: string) => {
        updateNodes((current) =>
          current.map((node) => {
            if (
              node.id !== nodeId ||
              node.type !== "variable" ||
              "variableId" in node ||
              (node as { objectSource?: string }).objectSource
            ) {
              return node;
            }
            if (
              node.valueType !== "location" &&
              node.valueType !== "transform"
            ) {
              return node;
            }
            const isExpanded = node.ports.some((port) => port.id.includes("."));
            return {
              ...node,
              ports: buildVariableLiteralPorts(
                node.valueType,
                !isExpanded,
                node.ports,
              ),
            };
          }),
        );
      },
      [buildVariableLiteralPorts, updateNodes],
    );

    const handleObjectFieldPathChange = useCallback(
      (nodeId: string, fieldPath: string) => {
        const updatedNodes = renderedNodes.map((node) => {
          if (node.id !== nodeId) {
            return node;
          }
          if (
            node.type === "operator" &&
            node.mode === "unary" &&
            node.operator === "get"
          ) {
            const nextValueType = getObjectFieldValueType(
              node.objectSource,
              fieldPath,
              node.objectFieldPathBase,
            );
            return {
              ...node,
              objectFieldPath: fieldPath,
              ports: node.ports.map((port) =>
                port.direction === "output"
                  ? { ...port, valueType: nextValueType, value: undefined }
                  : port,
              ),
            };
          }
          if (
            node.type === "operator" &&
            node.mode === "binary" &&
            node.operator === "find"
          ) {
            const nextInput2Type =
              node.listItemValueType === "object" && node.objectSource
                ? getObjectFieldValueType(
                    node.objectSource,
                    fieldPath,
                    node.objectFieldPathBase,
                  )
                : (node.listItemValueType ?? "string");
            return {
              ...node,
              objectFieldPath: fieldPath,
              ports: node.ports.map((port) =>
                port.id === "input2"
                  ? { ...port, valueType: nextInput2Type, value: undefined }
                  : port,
              ),
            };
          }
          if (node.type === "event") {
            return {
              ...node,
              objectFieldPath: fieldPath,
            };
          }
          return node;
        });
        const nextNodes = customObjectPathSchema
          ? synchronizeBehaviorObjectPaths(
              { nodes: updatedNodes, connections },
              customObjectPathSchema,
            ).nodes
          : updatedNodes;
        const nextConnections = connections.filter((connection) => {
          const fromNode = nextNodes.find(
            (node) => node.id === connection.fromNodeId,
          );
          const toNode = nextNodes.find(
            (node) => node.id === connection.toNodeId,
          );
          const fromPort = fromNode?.ports.find(
            (port) => port.id === connection.fromPortId,
          );
          const toPort = toNode?.ports.find(
            (port) => port.id === connection.toPortId,
          );
          if (!fromPort || !toPort) return false;
          return areBehaviorPortTypesCompatible(
            fromPort.valueType,
            toPort.valueType,
          );
        });
        if (onBehaviorGraphCommit) {
          onBehaviorGraphCommit({
            nodes: nextNodes,
            connections: nextConnections,
          });
          return;
        }
        updateNodes(() => nextNodes);
        if (nextConnections.length !== connections.length) {
          onConnectionsChange?.(nextConnections);
        }
      },
      [
        connections,
        customObjectPathSchema,
        getObjectFieldValueType,
        onBehaviorGraphCommit,
        onConnectionsChange,
        renderedNodes,
        updateNodes,
      ],
    );

    const handleEventTriggerOnInitChange = useCallback(
      (nodeId: string, triggerOnInit: boolean) => {
        updateNodes((current) =>
          current.map((node) => {
            if (node.id !== nodeId || node.type !== "event") {
              return node;
            }
            if (node.kind !== "onChange") {
              return node;
            }
            return {
              ...node,
              triggerOnInit,
            };
          }),
        );
      },
      [updateNodes],
    );

    const handleAnimPlayInfiniteChange = useCallback(
      (nodeId: string, playInfinite: boolean) => {
        const stripFlowOutConnections = (
          nextConnections: BehaviorConnection[],
        ) =>
          playInfinite
            ? nextConnections.filter(
                (connection) =>
                  !(
                    connection.fromNodeId === nodeId &&
                    connection.fromPortId === "out"
                  ),
              )
            : nextConnections;

        if (onBehaviorGraphCommit) {
          const nextNodes = renderedNodes.map((node) => {
            if (
              node.id !== nodeId ||
              node.type !== "action" ||
              node.kind !== "animPlay"
            ) {
              return node;
            }
            return { ...node, playInfinite };
          });
          const nextConnections = stripFlowOutConnections(connections);
          onBehaviorGraphCommit({
            nodes: nextNodes,
            connections: nextConnections,
          });
          if (!onNodesChange) {
            setInternalNodes(nextNodes);
          }
          return;
        }

        updateNodes((current) =>
          current.map((node) => {
            if (
              node.id !== nodeId ||
              node.type !== "action" ||
              node.kind !== "animPlay"
            ) {
              return node;
            }
            return { ...node, playInfinite };
          }),
        );
        if (playInfinite) {
          const nextConnections = stripFlowOutConnections(connections);
          if (nextConnections.length !== connections.length) {
            onConnectionsChange?.(nextConnections);
          }
        }
      },
      [
        connections,
        onBehaviorGraphCommit,
        onConnectionsChange,
        onNodesChange,
        renderedNodes,
        updateNodes,
      ],
    );

    const getKeyframePortLabels = useCallback(() => {
      const anim = behaviorDico.animation;
      return {
        flowIn: behaviorDico.actions.flowIn,
        flowOut: behaviorDico.actions.flowOut,
        offset: anim.offset,
        offsetOut: anim.offsetOut,
        easing: anim.easing,
        easingOut: anim.easingOut,
        mirrorSuffix: anim.mirrorSuffix,
        props: anim.props,
      };
    }, [behaviorDico]);

    const commitKeyframeGraph = useCallback(
      (nextNodes: BehaviorNode[], nextConnections: BehaviorConnection[]) => {
        if (onBehaviorGraphCommit) {
          onBehaviorGraphCommit({
            nodes: nextNodes,
            connections: nextConnections,
          });
          if (!onNodesChange) {
            setInternalNodes(nextNodes);
          }
          return;
        }
        updateNodes(() => nextNodes);
        if (nextConnections !== connections) {
          onConnectionsChange?.(nextConnections);
        }
      },
      [
        connections,
        onBehaviorGraphCommit,
        onConnectionsChange,
        onNodesChange,
        updateNodes,
      ],
    );

    const handleAddKeyframePort = useCallback(
      (portId: KeyframeAddablePortId) => {
        if (readOnly) return;
        const layoutDefaults = getAnimKeyframeDefaultsFromLayoutNode(node);
        const defaultValue =
          portId === "easing" ? "ease" : layoutDefaults[portId];
        const synced = addAnimKeyframePortAcrossGraph(
          renderedNodes,
          connections,
          portId,
          getKeyframePortLabels(),
          defaultValue,
        );
        commitKeyframeGraph(synced.nodes, synced.connections);
      },
      [
        commitKeyframeGraph,
        connections,
        getKeyframePortLabels,
        node,
        readOnly,
        renderedNodes,
      ],
    );

    const handleRemoveKeyframePort = useCallback(
      (portId: string) => {
        if (readOnly) return;
        const synced = removeAnimKeyframePortAcrossGraph(
          renderedNodes,
          connections,
          portId,
        );
        commitKeyframeGraph(synced.nodes, synced.connections);
      },
      [commitKeyframeGraph, connections, readOnly, renderedNodes],
    );

    const handleControlRoomFieldChange = useCallback(
      (
        nodeId: string,
        field:
          | "signalName"
          | "signalDescription"
          | "shortcutKey1"
          | "shortcutKey2"
          | "shortcutKey3"
          | "shortcutBinding"
          | "shortcutActionKey",
        value: string | undefined,
      ) => {
        updateNodes((current) =>
          current.map((node) => {
            if (node.id !== nodeId || node.type !== "event") return node;
            if (node.kind !== "onControlRoom") return node;
            return { ...node, [field]: value };
          }),
        );
      },
      [updateNodes],
    );

    const handleControlRoomShortcutChange = useCallback(
      (
        nodeId: string,
        shortcut: {
          shortcutKey1?: string;
          shortcutKey2?: string;
          shortcutKey3?: string;
        },
      ) => {
        updateNodes((current) =>
          current.map((node) => {
            if (node.id !== nodeId || node.type !== "event") return node;
            if (node.kind !== "onControlRoom") return node;
            return {
              ...node,
              shortcutKey1: shortcut.shortcutKey1,
              shortcutKey2: shortcut.shortcutKey2,
              shortcutKey3: shortcut.shortcutKey3,
            };
          }),
        );
      },
      [updateNodes],
    );

    const handleListItemValueTypeChange = useCallback(
      (nodeId: string, nextType: Exclude<VariableValueType, "list">) => {
        const connectedOperatorIds = new Set(
          connections
            .filter(
              (connection) =>
                connection.fromNodeId === nodeId &&
                connection.toPortId === "input1",
            )
            .map((connection) => connection.toNodeId),
        );
        const nextNodes = renderedNodes
          .map((currentNode) => {
            if (currentNode.id !== nodeId) return currentNode;
            if (
              currentNode.type === "variable" &&
              currentNode.valueType === "list"
            ) {
              return {
                ...currentNode,
                listItemValueType: nextType,
                ports: currentNode.ports.map((port) =>
                  port.id === "output"
                    ? { ...port, value: [] as ListValue }
                    : port,
                ),
              };
            }
            if (
              currentNode.type === "operator" &&
              (currentNode.operator === "push" ||
                currentNode.operator === "get" ||
                currentNode.operator === "remove" ||
                currentNode.operator === "find" ||
                currentNode.operator === "findByPath" ||
                currentNode.operator === "findByCondition" ||
                currentNode.operator === "filter" ||
                currentNode.operator === "sortBy" ||
                currentNode.operator === "sort" ||
                currentNode.operator === "take" ||
                currentNode.operator === "currentItem" ||
                currentNode.operator === "comparator")
            ) {
              return {
                ...currentNode,
                listItemValueType: nextType,
                ports: currentNode.ports.map((port) => {
                  if (currentNode.operator === "push" && port.id === "input2") {
                    return { ...port, valueType: nextType, value: undefined };
                  }
                  if (
                    (currentNode.operator === "get" ||
                      currentNode.operator === "find" ||
                      currentNode.operator === "findByPath" ||
                      currentNode.operator === "findByCondition" ||
                      currentNode.operator === "currentItem") &&
                    port.id === "output"
                  ) {
                    return { ...port, valueType: nextType, value: undefined };
                  }
                  if (
                    currentNode.operator === "comparator" &&
                    (port.id === "outputA" || port.id === "outputB")
                  ) {
                    return { ...port, valueType: nextType, value: undefined };
                  }
                  if (currentNode.operator === "find" && port.id === "input2") {
                    return { ...port, valueType: nextType, value: undefined };
                  }
                  if (
                    (currentNode.operator === "remove" ||
                      currentNode.operator === "filter" ||
                      currentNode.operator === "sortBy" ||
                      currentNode.operator === "sort" ||
                      currentNode.operator === "take") &&
                    port.id === "output"
                  ) {
                    return {
                      ...port,
                      valueType: "list",
                      value: [] as ListValue,
                    };
                  }
                  return port;
                }),
              };
            }
            return currentNode;
          })
          .map((currentNode) => {
            if (!connectedOperatorIds.has(currentNode.id)) {
              return currentNode;
            }
            if (currentNode.type !== "operator") {
              return currentNode;
            }
            if (
              currentNode.operator !== "push" &&
              currentNode.operator !== "get" &&
              currentNode.operator !== "remove" &&
              currentNode.operator !== "find" &&
              currentNode.operator !== "findByPath" &&
              currentNode.operator !== "findByCondition" &&
              currentNode.operator !== "filter" &&
              currentNode.operator !== "sort" &&
              currentNode.operator !== "take" &&
              currentNode.operator !== "currentItem" &&
              currentNode.operator !== "comparator"
            ) {
              return currentNode;
            }
            return {
              ...currentNode,
              listItemValueType: nextType,
              ports: currentNode.ports.map((port) => {
                if (currentNode.operator === "push" && port.id === "input2") {
                  return { ...port, valueType: nextType, value: undefined };
                }
                if (
                  (currentNode.operator === "get" ||
                    currentNode.operator === "find" ||
                    currentNode.operator === "findByPath" ||
                    currentNode.operator === "findByCondition" ||
                    currentNode.operator === "currentItem") &&
                  port.id === "output"
                ) {
                  return { ...port, valueType: nextType, value: undefined };
                }
                if (
                  currentNode.operator === "comparator" &&
                  (port.id === "outputA" || port.id === "outputB")
                ) {
                  return { ...port, valueType: nextType, value: undefined };
                }
                if (currentNode.operator === "find" && port.id === "input2") {
                  return { ...port, valueType: nextType, value: undefined };
                }
                if (
                  (currentNode.operator === "filter" ||
                    currentNode.operator === "sortBy" ||
                    currentNode.operator === "sort" ||
                    currentNode.operator === "take") &&
                  port.id === "output"
                ) {
                  return {
                    ...port,
                    valueType: "list",
                    value: [] as ListValue,
                  };
                }
                return port;
              }),
            };
          });

        const nextConnections = connections.filter((connection) => {
          const fromNode = nextNodes.find(
            (n) => n.id === connection.fromNodeId,
          );
          const toNode = nextNodes.find((n) => n.id === connection.toNodeId);
          const fromPort = fromNode?.ports.find(
            (p) => p.id === connection.fromPortId,
          );
          const toPort = toNode?.ports.find(
            (p) => p.id === connection.toPortId,
          );
          if (!fromPort || !toPort) return false;
          return areBehaviorPortTypesCompatible(
            fromPort.valueType,
            toPort.valueType,
          );
        });

        if (onBehaviorGraphCommit) {
          onBehaviorGraphCommit({
            nodes: nextNodes,
            connections: nextConnections,
          });
        } else {
          updateNodes(() => nextNodes);
          if (nextConnections.length !== connections.length) {
            onConnectionsChange?.(nextConnections);
          }
        }
      },
      [
        connections,
        onBehaviorGraphCommit,
        onConnectionsChange,
        renderedNodes,
        updateNodes,
      ],
    );

    const handleDatePartFormatChange = useCallback(
      (nodeId: string, format: DatePartTextFormat | undefined) => {
        const { mapped, stripOutgoingFromOutput } =
          applyDatePartFormatToBehaviorNodes(renderedNodes, nodeId, format);

        if (stripOutgoingFromOutput && onBehaviorGraphCommit) {
          const nextConnections = connections.filter(
            (connection) =>
              !(
                connection.fromNodeId === nodeId &&
                connection.fromPortId === "output"
              ),
          );
          onBehaviorGraphCommit({
            nodes: mapped,
            connections: nextConnections,
          });
          if (!onNodesChange) {
            setInternalNodes(mapped);
          }
          return;
        }

        updateNodes((current) => {
          const inner = applyDatePartFormatToBehaviorNodes(
            current,
            nodeId,
            format,
          );

          if (inner.stripOutgoingFromOutput) {
            const nextConnections = connections.filter(
              (c) => !(c.fromNodeId === nodeId && c.fromPortId === "output"),
            );
            if (nextConnections.length !== connections.length) {
              onConnectionsChange?.(nextConnections);
            }
          }

          return inner.mapped;
        });
      },
      [
        renderedNodes,
        connections,
        onBehaviorGraphCommit,
        onConnectionsChange,
        onNodesChange,
        updateNodes,
      ],
    );

    const handleDeleteNodes = useCallback(
      (nodeIds: string[]) => {
        if (nodeIds.length === 0) return;
        const protectedIds = new Set(
          scope === "objectGraph"
            ? renderedNodes
                .filter(
                  (n) =>
                    (n.type === "operator" && n.objectDefinition) ||
                    (n.type === "control" && n.controlType === "variableEnd"),
                )
                .map((n) => n.id)
            : [],
        );
        const selected = new Set(nodeIds.filter((id) => !protectedIds.has(id)));
        const nextConnections = connections.filter(
          (connection) =>
            !selected.has(connection.fromNodeId) &&
            !selected.has(connection.toNodeId),
        );
        if (nextConnections.length !== connections.length) {
          onConnectionsChange?.(nextConnections);
        }
        updateNodes((current) =>
          current.filter((node) => !selected.has(node.id)),
        );
        setSelectedNodeIds((current) =>
          current.filter((id) => !selected.has(id)),
        );
      },
      [connections, onConnectionsChange, updateNodes, scope, renderedNodes],
    );

    const handleDuplicateNodes = useCallback(
      (nodeIds: string[]) => {
        if (nodeIds.length === 0) return;
        const duplicates: BehaviorNode[] = [];
        const hasAnimBegin =
          scope === "scene" &&
          sceneAnimationGraphHasTerminalKind(
            { nodes: renderedNodes, connections },
            "animBegin",
          );
        const hasAnimEnd =
          scope === "scene" &&
          sceneAnimationGraphHasTerminalKind(
            { nodes: renderedNodes, connections },
            "animEnd",
          );
        const hasEventOutput =
          scope === "triggerGraph" &&
          triggerGraphHasEventOutput({ nodes: renderedNodes });
        const hasFunctionBegin =
          scope === "functionGraph" &&
          functionGraphHasFunctionBegin({ nodes: renderedNodes });
        const hasFunctionEnd =
          scope === "functionGraph" &&
          functionGraphHasFunctionEnd({ nodes: renderedNodes });
        const hasVariableBegin =
          scope === "variableGraph" &&
          variableGraphHasVariableBegin({ nodes: renderedNodes });
        const hasVariableEnd =
          scope === "variableGraph" &&
          variableGraphHasVariableEnd({ nodes: renderedNodes });
        nodeIds.forEach((nodeId, index) => {
          const source = nodesById.get(nodeId);
          if (!source) return;
          if (
            scope === "objectGraph" &&
            ((source.type === "operator" && source.objectDefinition) ||
              (source.type === "control" &&
                source.controlType === "variableEnd"))
          )
            return;
          if (
            scope === "scene" &&
            source.type === "action" &&
            source.kind === "animBegin" &&
            hasAnimBegin
          ) {
            return;
          }
          if (
            scope === "scene" &&
            source.type === "action" &&
            source.kind === "animEnd" &&
            hasAnimEnd
          ) {
            return;
          }
          if (
            scope === "triggerGraph" &&
            source.type === "control" &&
            source.controlType === "eventOutput" &&
            hasEventOutput
          ) {
            return;
          }
          if (
            scope === "functionGraph" &&
            source.type === "control" &&
            source.controlType === "functionBegin" &&
            hasFunctionBegin
          ) {
            return;
          }
          if (
            scope === "functionGraph" &&
            source.type === "control" &&
            source.controlType === "functionEnd" &&
            hasFunctionEnd
          ) {
            return;
          }
          if (
            scope === "variableGraph" &&
            source.type === "control" &&
            source.controlType === "variableBegin" &&
            hasVariableBegin
          ) {
            return;
          }
          if (
            scope === "variableGraph" &&
            source.type === "control" &&
            source.controlType === "variableEnd" &&
            hasVariableEnd
          ) {
            return;
          }
          if (
            isSelfReferencingCustomSceneEventNode(source, editingCustomEventId)
          ) {
            return;
          }
          duplicates.push({
            ...source,
            id: crypto.randomUUID(),
            x: source.x + 40 + index * 8,
            y: source.y + 40 + index * 8,
            ports: source.ports.map((port) => ({ ...port })),
          });
        });
        if (duplicates.length === 0) return;
        updateNodes((current) => [...current, ...duplicates]);
        setSelectedNodeIds(duplicates.map((node) => node.id));
      },
      [
        connections,
        editingCustomEventId,
        nodesById,
        renderedNodes,
        scope,
        updateNodes,
      ],
    );

    const handleCreateVariableFromSelection = useCallback(
      (nodeIds: string[]) => {
        if (
          readOnly ||
          scope !== "node" ||
          !onCreateVariableFromSelection ||
          !sceneCatalog
        ) {
          return;
        }

        const variableId = crypto.randomUUID();
        const defaultName = allocateUniqueCatalogName(
          "variables",
          canvasDico.createVariableFromSelectionDefaultName(
            sceneCatalog.variables.length + 1,
          ),
          sceneCatalog,
        );
        const { width: canvasWidth, height: canvasHeight } =
          getBehaviorCanvasViewportSize();
        const extraction = extractBehaviorSelectionToSceneVariable(
          { nodes: renderedNodes, connections },
          nodeIds,
          {
            variableId,
            variableName: defaultName,
            valuePortLabel: behaviorDico.controls.value,
            refOutputLabel: behaviorDico.controls.value,
            canvasWidth,
            canvasHeight,
          },
        );
        if (!extraction) return;

        onCreateVariableFromSelection({
          catalog: {
            ...sceneCatalog,
            variables: [...sceneCatalog.variables, extraction.catalogEntry],
          },
          parentGraph: extraction.parentGraph,
        });

        const variableRefNode = extraction.parentGraph.nodes.find(
          (behaviorNode) =>
            behaviorNode.type === "variable" &&
            "variableId" in behaviorNode &&
            behaviorNode.variableId === variableId,
        );
        if (variableRefNode) {
          setSelectedNodeIds([variableRefNode.id]);
        }
        return;
      },
      [
        readOnly,
        scope,
        onCreateVariableFromSelection,
        sceneCatalog,
        renderedNodes,
        connections,
        canvasDico,
        behaviorDico.controls.value,
      ],
    );

    const handleCreateFunctionFromSelection = useCallback(
      (nodeIds: string[]) => {
        if (
          readOnly ||
          scope !== "node" ||
          !onCreateFunctionFromSelection ||
          !sceneCatalog
        ) {
          return;
        }

        const functionId = crypto.randomUUID();
        const defaultName = allocateUniqueCatalogName(
          "functions",
          canvasDico.createFunctionFromSelectionDefaultName(
            sceneCatalog.functions.length + 1,
          ),
          sceneCatalog,
        );
        const { width: canvasWidth, height: canvasHeight } =
          getBehaviorCanvasViewportSize();
        const extraction = extractBehaviorSelectionToSceneFunction(
          { nodes: renderedNodes, connections },
          nodeIds,
          {
            functionId,
            functionName: defaultName,
            flowInLabel: behaviorDico.actions.flowIn,
            flowOutLabel: behaviorDico.actions.flowOut,
            canvasWidth,
            canvasHeight,
          },
        );
        if (!extraction) return;

        onCreateFunctionFromSelection({
          catalog: {
            ...sceneCatalog,
            functions: [...sceneCatalog.functions, extraction.catalogEntry],
          },
          parentGraph: extraction.parentGraph,
        });

        const callNode = extraction.parentGraph.nodes.find(
          (behaviorNode) =>
            behaviorNode.type === "action" &&
            behaviorNode.kind === "call" &&
            behaviorNode.functionId === functionId,
        );
        if (callNode) {
          setSelectedNodeIds([callNode.id]);
        }
      },
      [
        readOnly,
        scope,
        onCreateFunctionFromSelection,
        sceneCatalog,
        renderedNodes,
        connections,
        canvasDico,
        behaviorDico.actions.flowIn,
        behaviorDico.actions.flowOut,
      ],
    );

    const buildBehaviorPayloadFromNodeIds = useCallback(
      (nodeIds: string[]): BehaviorClipboardPayload | null => {
        if (nodeIds.length === 0) return null;
        const selectedSet = new Set(nodeIds);
        const selectedNodes = renderedNodes
          .filter((behaviorNode) => selectedSet.has(behaviorNode.id))
          .map((behaviorNode) => ({
            ...behaviorNode,
            ports: behaviorNode.ports.map((port) => ({ ...port })),
          }));
        if (selectedNodes.length === 0) return null;
        const selectedConnections = connections
          .filter(
            (connection) =>
              selectedSet.has(connection.fromNodeId) &&
              selectedSet.has(connection.toNodeId),
          )
          .map((connection) => ({ ...connection }));
        return {
          kind: "meet-manager/editor-clipboard",
          version: 1,
          entity: "behavior",
          data: {
            nodes: selectedNodes,
            connections: selectedConnections,
          },
        };
      },
      [connections, renderedNodes],
    );

    const writeBehaviorPayloadToClipboard = useCallback(
      async (payload: BehaviorClipboardPayload) => {
        setInMemoryClipboardPayload(payload);
        setHasBehaviorClipboardPayload(true);
        await writeClipboardPayloadToSystem(payload);
      },
      [],
    );

    const copySelectionToPayload =
      useCallback((): BehaviorClipboardPayload | null => {
        return buildBehaviorPayloadFromNodeIds(selectedNodeIds);
      }, [buildBehaviorPayloadFromNodeIds, selectedNodeIds]);

    const cutSelectionToPayload =
      useCallback((): BehaviorClipboardPayload | null => {
        const payload = copySelectionToPayload();
        if (!payload) return null;
        handleDeleteNodes(selectedNodeIds);
        return payload;
      }, [copySelectionToPayload, handleDeleteNodes, selectedNodeIds]);

    const pasteBehaviorPayload = useCallback(
      (payload: BehaviorClipboardPayload): string[] => {
        if (payload.data.nodes.length === 0) return [];
        const hasAnimBegin =
          scope === "scene" &&
          sceneAnimationGraphHasTerminalKind(
            { nodes: renderedNodes, connections },
            "animBegin",
          );
        const hasAnimEnd =
          scope === "scene" &&
          sceneAnimationGraphHasTerminalKind(
            { nodes: renderedNodes, connections },
            "animEnd",
          );
        const hasEventOutput =
          scope === "triggerGraph" &&
          triggerGraphHasEventOutput({ nodes: renderedNodes });
        const hasFunctionBegin =
          scope === "functionGraph" &&
          functionGraphHasFunctionBegin({ nodes: renderedNodes });
        const hasFunctionEnd =
          scope === "functionGraph" &&
          functionGraphHasFunctionEnd({ nodes: renderedNodes });
        const hasVariableBegin =
          scope === "variableGraph" &&
          variableGraphHasVariableBegin({ nodes: renderedNodes });
        const hasVariableEnd =
          scope === "variableGraph" &&
          variableGraphHasVariableEnd({ nodes: renderedNodes });
        const nodesToPaste =
          scope === "objectGraph"
            ? payload.data.nodes.filter(
                (n) =>
                  n.type === "variable" ||
                  (n.type === "operator" && !n.objectDefinition),
              )
            : scope === "scene"
              ? payload.data.nodes.filter((sourceNode) => {
                  if (sourceNode.type !== "action") return true;
                  if (sourceNode.kind === "animBegin" && hasAnimBegin) {
                    return false;
                  }
                  if (sourceNode.kind === "animEnd" && hasAnimEnd) {
                    return false;
                  }
                  return true;
                })
              : scope === "triggerGraph"
                ? payload.data.nodes.filter((sourceNode) => {
                    if (
                      sourceNode.type === "control" &&
                      sourceNode.controlType === "eventOutput" &&
                      hasEventOutput
                    ) {
                      return false;
                    }
                    if (
                      isSelfReferencingCustomSceneEventNode(
                        sourceNode,
                        editingCustomEventId,
                      )
                    ) {
                      return false;
                    }
                    return true;
                  })
                : scope === "functionGraph"
                  ? payload.data.nodes.filter((sourceNode) => {
                      if (
                        sourceNode.type === "control" &&
                        sourceNode.controlType === "functionBegin" &&
                        hasFunctionBegin
                      ) {
                        return false;
                      }
                      if (
                        sourceNode.type === "control" &&
                        sourceNode.controlType === "functionEnd" &&
                        hasFunctionEnd
                      ) {
                        return false;
                      }
                      return true;
                    })
                  : scope === "variableGraph"
                    ? payload.data.nodes.filter((sourceNode) => {
                        if (
                          sourceNode.type === "control" &&
                          sourceNode.controlType === "variableBegin" &&
                          hasVariableBegin
                        ) {
                          return false;
                        }
                        if (
                          sourceNode.type === "control" &&
                          sourceNode.controlType === "variableEnd" &&
                          hasVariableEnd
                        ) {
                          return false;
                        }
                        return true;
                      })
                    : payload.data.nodes;
        if (nodesToPaste.length === 0) return [];
        const sourceNodeIds = new Set(nodesToPaste.map((node) => node.id));
        const idMap = new Map<string, string>();
        const sourceMinX = Math.min(...nodesToPaste.map((node) => node.x));
        const sourceMinY = Math.min(...nodesToPaste.map((node) => node.y));
        const anchor = lastCursorWorldPositionRef.current;
        const pastedNodes = nodesToPaste.map((sourceNode, index) => {
          const nextId = crypto.randomUUID();
          idMap.set(sourceNode.id, nextId);
          const nextX =
            anchor != null
              ? sourceNode.x - sourceMinX + anchor.x + index * 8
              : sourceNode.x + 40 + index * 8;
          const nextY =
            anchor != null
              ? sourceNode.y - sourceMinY + anchor.y + index * 8
              : sourceNode.y + 40 + index * 8;
          return {
            ...sourceNode,
            id: nextId,
            x: nextX,
            y: nextY,
            ports: sourceNode.ports.map((port: Port) => ({ ...port })),
          };
        });
        const pastedConnections = payload.data.connections
          .filter(
            (connection) =>
              sourceNodeIds.has(connection.fromNodeId) &&
              sourceNodeIds.has(connection.toNodeId),
          )
          .map((connection) => {
            const nextFromNodeId = idMap.get(connection.fromNodeId);
            const nextToNodeId = idMap.get(connection.toNodeId);
            if (!nextFromNodeId || !nextToNodeId) {
              return null;
            }
            return {
              ...connection,
              id: `${nextFromNodeId}:${connection.fromPortId}->${nextToNodeId}:${connection.toPortId}`,
              fromNodeId: nextFromNodeId,
              toNodeId: nextToNodeId,
            };
          })
          .filter(
            (connection): connection is BehaviorConnection =>
              connection != null,
          );

        const nextNodes = [...renderedNodes, ...pastedNodes];
        const nextConnections = [...connections, ...pastedConnections];
        if (onBehaviorGraphCommit) {
          onBehaviorGraphCommit({
            nodes: nextNodes,
            connections: nextConnections,
          });
        } else {
          updateNodes(() => nextNodes);
          if (pastedConnections.length > 0) {
            onConnectionsChange?.(nextConnections);
          }
        }
        const pastedIds = pastedNodes.map((node) => node.id);
        setSelectedNodeIds(pastedIds);
        setHasBehaviorClipboardPayload(true);
        return pastedIds;
      },
      [
        connections,
        editingCustomEventId,
        onBehaviorGraphCommit,
        onConnectionsChange,
        renderedNodes,
        scope,
        updateNodes,
      ],
    );

    const handleCopyNodes = useCallback(
      async (nodeIds: string[]) => {
        const payload = buildBehaviorPayloadFromNodeIds(nodeIds);
        if (!payload) return;
        await writeBehaviorPayloadToClipboard(payload);
      },
      [buildBehaviorPayloadFromNodeIds, writeBehaviorPayloadToClipboard],
    );

    const handleCutNodes = useCallback(
      async (nodeIds: string[]) => {
        const payload = buildBehaviorPayloadFromNodeIds(nodeIds);
        if (!payload) return;
        await writeBehaviorPayloadToClipboard(payload);
        handleDeleteNodes(nodeIds);
      },
      [
        buildBehaviorPayloadFromNodeIds,
        handleDeleteNodes,
        writeBehaviorPayloadToClipboard,
      ],
    );

    const handlePasteFromClipboard = useCallback(async () => {
      const inMemoryPayload = getInMemoryClipboardPayload();
      if (inMemoryPayload?.entity === "behavior") {
        pasteBehaviorPayload(inMemoryPayload);
        return;
      }
      const systemPayload = await readClipboardPayloadFromSystem();
      if (systemPayload?.entity === "behavior") {
        setInMemoryClipboardPayload(systemPayload);
        setHasBehaviorClipboardPayload(true);
        pasteBehaviorPayload(systemPayload);
      }
    }, [pasteBehaviorPayload]);

    useImperativeHandle(
      ref,
      (): BehaviorCanvasClipboardHandle => ({
        hasSelection: () => selectedNodeIds.length > 0,
        copySelectionToPayload,
        cutSelectionToPayload,
        pasteBehaviorPayload,
      }),
      [
        copySelectionToPayload,
        cutSelectionToPayload,
        pasteBehaviorPayload,
        selectedNodeIds.length,
      ],
    );

    useEffect(() => {
      const isEditableTarget = (target: EventTarget | null): boolean => {
        if (!(target instanceof HTMLElement)) return false;
        const tagName = target.tagName.toLowerCase();
        return (
          target.isContentEditable ||
          tagName === "input" ||
          tagName === "textarea" ||
          tagName === "select"
        );
      };
      const onKeyDown = (event: KeyboardEvent) => {
        if (!editorActiveRef.current) return;
        if (readOnly) return;
        if (
          (event.key !== "Delete" && event.key !== "Backspace") ||
          selectedNodeIds.length === 0
        ) {
          return;
        }
        if (isEditableTarget(event.target)) {
          return;
        }
        event.preventDefault();
        handleDeleteNodes(selectedNodeIds);
      };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    }, [selectedNodeIds, handleDeleteNodes, readOnly]);

    useEffect(() => {
      const onFocus = () => {
        setHasBehaviorClipboardPayload(
          isClipboardPayloadForScope(getInMemoryClipboardPayload(), "behavior"),
        );
      };
      window.addEventListener("focus", onFocus);
      return () => {
        window.removeEventListener("focus", onFocus);
      };
    }, []);

    const connectedPortsSet = useMemo(() => {
      const connected = new Set<string>();
      connections.forEach((connection) => {
        connected.add(`${connection.fromNodeId}:${connection.fromPortId}`);
        connected.add(`${connection.toNodeId}:${connection.toPortId}`);
      });
      return connected;
    }, [connections]);
    const isPortConnected = useCallback(
      (nodeId: string, portId: string) =>
        connectedPortsSet.has(`${nodeId}:${portId}`),
      [connectedPortsSet],
    );
    const sceneTransform = `translate(${viewportOffset.x}px, ${viewportOffset.y}px) scale(${viewportZoom})`;

    const behaviorFontContextValue = useMemo(
      () => ({
        customFontFamilies: customFontFamilies ?? [],
        onPickFont,
      }),
      [customFontFamilies, onPickFont],
    );

    return (
      <SceneObjectTypesProvider catalog={sceneCatalog}>
        <BehaviorPathSchemaProvider schema={effectiveObjectPathSchema}>
          <FilterSvgPickerContext.Provider value={onPickFilterSvg}>
            <BehaviorFontContext.Provider value={behaviorFontContextValue}>
              <Box
                data-behavior-workspace={modern ? "modern" : "original"}
                sx={{
                  display: "flex",
                  width: "100%",
                  height: "100%",
                  minHeight: 0,
                  overflow: "hidden",
                  ...(modern ? behaviorPresentation(colorMode) : {}),
                }}
              >
                <BehaviorNodeList
                  node={node}
                  scope={scope}
                  allowRowIndexVariable={allowRowIndexVariable}
                  layoutListBindingSource={layoutListBindingSource}
                  readOnly={readOnly}
                  sceneGraphNodes={
                    scope === "scene" ||
                    scope === "triggerGraph" ||
                    scope === "functionGraph" ||
                    scope === "variableGraph"
                      ? renderedNodes
                      : undefined
                  }
                />
                <div
                  data-behavior-canvas
                  ref={canvasRef}
                  onMouseDown={handleCanvasMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleCanvasMouseUp}
                  onMouseLeave={handleCanvasMouseUp}
                  onWheel={handleCanvasWheel}
                  onContextMenu={(event) => {
                    event.preventDefault();
                    const target = event.target as HTMLElement;
                    if (
                      target.closest("[data-node-root]") ||
                      target.closest("[data-port-id]")
                    ) {
                      return;
                    }
                    lastCursorWorldPositionRef.current =
                      getWorldPointerPosition(event.clientX, event.clientY);
                    openBehaviorNodeMenu(
                      event.clientX + 2,
                      event.clientY - 6,
                      selectedNodeIds,
                    );
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={handleCanvasDrop}
                  style={{
                    position: "relative",
                    flex: 1,
                    minWidth: 0,
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    overscrollBehavior: "contain",
                    zIndex: 1,
                    backgroundColor: "#ffffff",
                    backgroundImage:
                      "linear-gradient(rgba(0, 0, 0, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 0, 0, 0.1) 1px, transparent 1px)",
                    backgroundSize: `${(modern ? 20 : 24) * viewportZoom}px ${(modern ? 20 : 24) * viewportZoom}px`,
                    backgroundPosition: `${viewportOffset.x}px ${viewportOffset.y}px`,
                    cursor: dragViewport ? "grabbing" : "default",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      transform: sceneTransform,
                      transformOrigin: "top left",
                    }}
                  >
                    <svg
                      width="100%"
                      height="100%"
                      style={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "auto",
                        overflow: "visible",
                      }}
                      aria-hidden="true"
                    >
                      {displayedConnections.map((connection) => {
                        const fromNode = nodesById.get(connection.fromNodeId);
                        const toNode = nodesById.get(connection.toNodeId);
                        if (!fromNode || !toNode) {
                          return null;
                        }

                        const fromPort = portsByNodeId
                          .get(fromNode.id)
                          ?.get(connection.fromPortId);
                        const toPort = portsByNodeId
                          .get(toNode.id)
                          ?.get(connection.toPortId);
                        if (!fromPort || !toPort) {
                          return null;
                        }

                        const fromPosition = portPositionsByKey.get(
                          `${fromNode.id}:${fromPort.id}`,
                        );
                        const toPosition = portPositionsByKey.get(
                          `${toNode.id}:${toPort.id}`,
                        );
                        if (!fromPosition || !toPosition) {
                          return null;
                        }
                        const curvePath = getCurvePath(
                          fromPosition,
                          toPosition,
                        );
                        return (
                          <g key={connection.id}>
                            <path
                              d={curvePath}
                              stroke="transparent"
                              strokeWidth={14}
                              fill="none"
                              pointerEvents="stroke"
                              onClick={(event) =>
                                handleConnectionClick(event, connection.id)
                              }
                              style={{
                                cursor: isDeleteModifierPressed
                                  ? deleteConnectionCursor
                                  : "default",
                              }}
                            />
                            <path
                              d={curvePath}
                              stroke={getValueTypeColor(fromPort.valueType)}
                              strokeWidth={2}
                              fill="none"
                              pointerEvents="none"
                            />
                          </g>
                        );
                      })}
                      {(() => {
                        if (!dragConnection) {
                          return null;
                        }
                        const fromNode = nodesById.get(dragConnection.nodeId);
                        if (!fromNode) {
                          return null;
                        }
                        const fromPosition =
                          portPositionsByKey.get(
                            `${fromNode.id}:${dragConnection.port.id}`,
                          ) ?? getPortPosition(fromNode, dragConnection.port);
                        if (!fromPosition) {
                          return null;
                        }
                        return (
                          <path
                            key="dragging-connection"
                            d={getCurvePath(fromPosition, dragConnection.to)}
                            stroke={getValueTypeColor(
                              dragConnection.port.valueType,
                            )}
                            strokeWidth={2}
                            fill="none"
                            strokeDasharray="6 4"
                          />
                        );
                      })()}
                    </svg>

                    {displayedNodes.map((node) => {
                      const opensSceneCatalogEntry = Boolean(
                        onOpenSceneCatalogEntry &&
                          resolveSceneCatalogDetailRoute(node, sceneCatalog),
                      );
                      return (
                        <div
                          key={node.id}
                          ref={(element) => {
                            if (element) {
                              nodeElementsRef.current.set(node.id, element);
                            } else {
                              nodeElementsRef.current.delete(node.id);
                            }
                          }}
                          onMouseDown={(event) =>
                            handleNodeMouseDown(event, node.id)
                          }
                          onDoubleClick={(event) =>
                            handleNodeDoubleClick(event, node.id)
                          }
                          onContextMenu={(event) =>
                            handleNodeContextMenu(event, node.id)
                          }
                          style={{
                            ...(modern
                              ? {
                                  "--behavior-node-accent":
                                    node.type === "event"
                                      ? palette.error
                                      : node.type === "action"
                                        ? palette.success
                                        : node.type === "control"
                                          ? palette.warning
                                          : palette.accent,
                                }
                              : {}),
                            position: "absolute",
                            left: node.x,
                            top: node.y,
                            cursor: opensSceneCatalogEntry ? "pointer" : "grab",
                            outline: selectedNodeIds.includes(node.id)
                              ? "2px solid rgb(69, 150, 231)"
                              : "none",
                            borderRadius: 8,
                          }}
                          data-node-root="true"
                        >
                          {node.type === "operator" ? (
                            <OperatorNode
                              onTimerPortsChange={
                                readOnly
                                  ? undefined
                                  : (id, ports) =>
                                      updateNodes((current) =>
                                        current.map((n) =>
                                          n.id === id ? { ...n, ports } : n,
                                        ),
                                      )
                              }
                              setPortsMap={setPortsMap}
                              node={node}
                              onPortMouseDown={handlePortMouseDown}
                              onPortMouseUp={handlePortMouseUp}
                              isPortConnected={isPortConnected}
                              onPortValueChange={
                                handlePortValueChange as (
                                  nodeId: string,
                                  portId: string,
                                  value: unknown,
                                ) => void
                              }
                              onObjectPortsChange={
                                scope === "objectGraph" && !readOnly
                                  ? handleObjectPortsChange
                                  : undefined
                              }
                              onDatePartFormatChange={
                                handleDatePartFormatChange
                              }
                              onListItemValueTypeChange={
                                handleListItemValueTypeChange
                              }
                              onObjectFieldPathChange={
                                handleObjectFieldPathChange
                              }
                            />
                          ) : node.type === "event" ? (
                            <EventNode
                              node={node}
                              setPortsMap={setPortsMap}
                              onPortMouseDown={handlePortMouseDown}
                              onPortMouseUp={handlePortMouseUp}
                              onObjectFieldPathChange={
                                handleObjectFieldPathChange
                              }
                              onTriggerOnInitChange={
                                handleEventTriggerOnInitChange
                              }
                              onControlRoomFieldChange={
                                handleControlRoomFieldChange
                              }
                              onControlRoomShortcutChange={
                                handleControlRoomShortcutChange
                              }
                              customEventName={
                                node.kind === "onCustomSceneEvent"
                                  ? sceneCatalog?.customEvents?.find(
                                      (entry) =>
                                        entry.id === node.customEventId,
                                    )?.name
                                  : undefined
                              }
                            />
                          ) : node.type === "control" ? (
                            node.controlType === "wait" ? (
                              <ControlWaitNode
                                node={node}
                                setPortsMap={setPortsMap}
                                onPortMouseDown={handlePortMouseDown}
                                onPortMouseUp={handlePortMouseUp}
                                isPortConnected={isPortConnected}
                                onPortValueChange={
                                  handlePortValueChange as (
                                    nodeId: string,
                                    portId: string,
                                    value: unknown,
                                  ) => void
                                }
                              />
                            ) : node.controlType === "repeat" ? (
                              <ControlRepeatNode
                                node={node}
                                setPortsMap={setPortsMap}
                                onPortMouseDown={handlePortMouseDown}
                                onPortMouseUp={handlePortMouseUp}
                                isPortConnected={isPortConnected}
                                onPortValueChange={
                                  handlePortValueChange as (
                                    nodeId: string,
                                    portId: string,
                                    value: unknown,
                                  ) => void
                                }
                              />
                            ) : node.controlType === "mergeFlows" ? (
                              <ControlMergeFlowsNode
                                node={node}
                                setPortsMap={setPortsMap}
                                onPortMouseDown={handlePortMouseDown}
                                onPortMouseUp={handlePortMouseUp}
                                isPortConnected={isPortConnected}
                              />
                            ) : node.controlType === "eventOutput" ? (
                              <EventOutputNode
                                node={node}
                                setPortsMap={setPortsMap}
                                onPortMouseDown={handlePortMouseDown}
                                onPortMouseUp={handlePortMouseUp}
                                isPortConnected={isPortConnected}
                              />
                            ) : node.controlType === "functionBegin" ? (
                              <FunctionBeginNode
                                node={node}
                                setPortsMap={setPortsMap}
                                onPortMouseDown={handlePortMouseDown}
                                onPortMouseUp={handlePortMouseUp}
                                isPortConnected={isPortConnected}
                                onAddInput={
                                  scope === "functionGraph" && !readOnly
                                    ? handleAddFunctionInput
                                    : undefined
                                }
                                onRenameInput={
                                  scope === "functionGraph" && !readOnly
                                    ? handleRenameFunctionInput
                                    : undefined
                                }
                                onRemoveInput={
                                  scope === "functionGraph" && !readOnly
                                    ? handleRemoveFunctionInput
                                    : undefined
                                }
                                readOnly={readOnly}
                              />
                            ) : node.controlType === "functionEnd" ? (
                              <FunctionEndNode
                                node={node}
                                setPortsMap={setPortsMap}
                                onPortMouseDown={handlePortMouseDown}
                                onPortMouseUp={handlePortMouseUp}
                                isPortConnected={isPortConnected}
                                onAddOutput={
                                  scope === "functionGraph" && !readOnly
                                    ? handleAddFunctionOutput
                                    : undefined
                                }
                                onRenameOutput={
                                  scope === "functionGraph" && !readOnly
                                    ? handleRenameFunctionOutput
                                    : undefined
                                }
                                onRemoveOutput={
                                  scope === "functionGraph" && !readOnly
                                    ? handleRemoveFunctionOutput
                                    : undefined
                                }
                                readOnly={readOnly}
                              />
                            ) : node.controlType === "variableBegin" ? (
                              <VariableBeginNode
                                node={node}
                                setPortsMap={setPortsMap}
                                onPortMouseDown={handlePortMouseDown}
                                onPortMouseUp={handlePortMouseUp}
                                isPortConnected={isPortConnected}
                                onAddInput={
                                  scope === "variableGraph" && !readOnly
                                    ? handleAddVariableInput
                                    : undefined
                                }
                                onRenameInput={
                                  scope === "variableGraph" && !readOnly
                                    ? handleRenameVariableInput
                                    : undefined
                                }
                                onRemoveInput={
                                  scope === "variableGraph" && !readOnly
                                    ? handleRemoveVariableInput
                                    : undefined
                                }
                                readOnly={readOnly}
                              />
                            ) : node.controlType === "variableEnd" &&
                              scope === "objectGraph" ? (
                              <ObjectEndNode
                                node={node}
                                setPortsMap={setPortsMap}
                                onPortMouseDown={handlePortMouseDown}
                                onPortMouseUp={handlePortMouseUp}
                                isPortConnected={isPortConnected}
                              />
                            ) : node.controlType === "variableEnd" ? (
                              <VariableEndNode
                                node={node}
                                setPortsMap={setPortsMap}
                                onPortMouseDown={handlePortMouseDown}
                                onPortMouseUp={handlePortMouseUp}
                                isPortConnected={isPortConnected}
                                onChangeValueType={
                                  scope === "variableGraph" && !readOnly
                                    ? handleChangeVariableOutputType
                                    : undefined
                                }
                                readOnly={readOnly}
                              />
                            ) : (
                              <ControlIfElseNode
                                node={node}
                                setPortsMap={setPortsMap}
                                onPortMouseDown={handlePortMouseDown}
                                onPortMouseUp={handlePortMouseUp}
                                isPortConnected={isPortConnected}
                                onPortValueChange={
                                  handlePortValueChange as (
                                    nodeId: string,
                                    portId: string,
                                    value: unknown,
                                  ) => void
                                }
                              />
                            )
                          ) : node.type === "action" &&
                            (node.kind === "animBegin" ||
                              node.kind === "animKeyframe" ||
                              node.kind === "animEnd" ||
                              node.kind === "animPlay") ? (
                            <ActionAnimationNode
                              node={node}
                              sceneAnimationNames={sceneAnimationNames}
                              setPortsMap={setPortsMap}
                              onPortMouseDown={handlePortMouseDown}
                              onPortMouseUp={handlePortMouseUp}
                              isPortConnected={isPortConnected}
                              onPortValueChange={
                                handlePortValueChange as (
                                  nodeId: string,
                                  portId: string,
                                  value: unknown,
                                ) => void
                              }
                              onPlayInfiniteChange={
                                readOnly
                                  ? undefined
                                  : handleAnimPlayInfiniteChange
                              }
                              onAddKeyframePort={
                                readOnly ? undefined : handleAddKeyframePort
                              }
                              onRemoveKeyframePort={
                                readOnly ? undefined : handleRemoveKeyframePort
                              }
                              readOnly={readOnly}
                            />
                          ) : node.type === "action" && node.kind === "set" ? (
                            <ActionSetNode
                              node={node}
                              setPortsMap={setPortsMap}
                              onPortMouseDown={handlePortMouseDown}
                              onPortMouseUp={handlePortMouseUp}
                              isPortConnected={isPortConnected}
                              onPortValueChange={
                                handlePortValueChange as (
                                  nodeId: string,
                                  portId: string,
                                  value: unknown,
                                ) => void
                              }
                              onToggleExpandedPorts={handleToggleExpandedPorts}
                            />
                          ) : node.type === "action" &&
                            node.kind === "createRowVariant" ? (
                            <ActionLayoutListNode
                              node={node}
                              rowVariantsNames={[]}
                              onCreateRowVariantNameBlur={undefined}
                              setPortsMap={setPortsMap}
                              onPortMouseDown={handlePortMouseDown}
                              onPortMouseUp={handlePortMouseUp}
                              onPortValueChange={
                                handlePortValueChange as (
                                  nodeId: string,
                                  portId: string,
                                  value: unknown,
                                ) => void
                              }
                              isPortConnected={isPortConnected}
                            />
                          ) : node.type === "action" && node.kind === "call" ? (
                            <FunctionCallNode
                              node={
                                node as import("../../../util/src/blueprint/types.js").FunctionCallActionNode
                              }
                              setPortsMap={setPortsMap}
                              onPortMouseDown={handlePortMouseDown}
                              onPortMouseUp={handlePortMouseUp}
                              onPortValueChange={
                                handlePortValueChange as (
                                  nodeId: string,
                                  portId: string,
                                  value: unknown,
                                ) => void
                              }
                              isPortConnected={isPortConnected}
                              functionName={
                                sceneCatalog?.functions?.find(
                                  (f) =>
                                    f.id ===
                                    (node as { functionId?: string })
                                      .functionId,
                                )?.name
                              }
                            />
                          ) : node.type === "action" &&
                            node.kind === "setVariable" ? (
                            <SetVariableNode
                              node={
                                node as import("../../../util/src/blueprint/types.js").SetVariableActionNode
                              }
                              setPortsMap={setPortsMap}
                              onPortMouseDown={handlePortMouseDown}
                              onPortMouseUp={handlePortMouseUp}
                              onPortValueChange={
                                handlePortValueChange as (
                                  nodeId: string,
                                  portId: string,
                                  value: unknown,
                                ) => void
                              }
                              isPortConnected={isPortConnected}
                              variableName={
                                sceneCatalog?.variables?.find(
                                  (v) =>
                                    v.id ===
                                    (node as { variableId?: string })
                                      .variableId,
                                )?.name
                              }
                            />
                          ) : node.type === "variable" &&
                            "variableId" in node ? (
                            <VariableRefNodeComponent
                              node={
                                node as unknown as import("../../../util/src/blueprint/types.js").VariableRefNode
                              }
                              setPortsMap={setPortsMap}
                              onPortMouseDown={handlePortMouseDown}
                              onPortMouseUp={handlePortMouseUp}
                              isPortConnected={isPortConnected}
                              onPortValueChange={
                                handlePortValueChange as (
                                  nodeId: string,
                                  portId: string,
                                  value: unknown,
                                ) => void
                              }
                              variableName={
                                sceneCatalog?.variables?.find(
                                  (v) =>
                                    v.id ===
                                    (node as { variableId?: string })
                                      .variableId,
                                )?.name
                              }
                              outputValueType={
                                sceneCatalog?.variables?.find(
                                  (v) =>
                                    v.id ===
                                    (node as { variableId?: string })
                                      .variableId,
                                )?.outputValueType
                              }
                            />
                          ) : (
                            <VariableNode
                              node={node}
                              layoutListTargetOptions={layoutListTargetOptions}
                              setPortsMap={setPortsMap}
                              onPortMouseDown={handlePortMouseDown}
                              onPortMouseUp={handlePortMouseUp}
                              isPortConnected={isPortConnected}
                              onPortValueChange={handlePortValueChange}
                              onToggleExpandedPorts={
                                handleToggleExpandedVariablePorts
                              }
                              onListItemValueTypeChange={
                                handleListItemValueTypeChange
                              }
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {selectionRect && (
                    <div
                      style={{
                        position: "absolute",
                        left:
                          Math.min(
                            selectionRect.start.x,
                            selectionRect.current.x,
                          ) *
                            viewportZoom +
                          viewportOffset.x,
                        top:
                          Math.min(
                            selectionRect.start.y,
                            selectionRect.current.y,
                          ) *
                            viewportZoom +
                          viewportOffset.y,
                        width:
                          Math.abs(
                            selectionRect.current.x - selectionRect.start.x,
                          ) * viewportZoom,
                        height:
                          Math.abs(
                            selectionRect.current.y - selectionRect.start.y,
                          ) * viewportZoom,
                        border: "1px solid rgba(69, 150, 231, 0.9)",
                        backgroundColor: "rgba(69, 150, 231, 0.15)",
                        pointerEvents: "none",
                        zIndex: 50,
                      }}
                    />
                  )}
                  <BehaviorContextMenu
                    menuState={nodeMenu}
                    selectionNodeIds={nodeMenu?.selectionNodeIds ?? []}
                    cutLabel={canvasDico.cut}
                    copyLabel={canvasDico.copy}
                    pasteLabel={canvasDico.paste}
                    duplicateLabel={canvasDico.duplicate}
                    deleteLabel={canvasDico.delete}
                    createVariableFromSelectionLabel={
                      nodeMenu?.canCreateVariable
                        ? canvasDico.createVariableFromSelection
                        : undefined
                    }
                    createFunctionFromSelectionLabel={
                      nodeMenu?.canCreateFunction
                        ? canvasDico.createFunctionFromSelection
                        : undefined
                    }
                    canPaste={readOnly ? false : hasBehaviorClipboardPayload}
                    onClose={() => setNodeMenu(null)}
                    onCut={
                      readOnly
                        ? () => {}
                        : (nodeIds) => void handleCutNodes(nodeIds)
                    }
                    onCopy={(nodeIds) => void handleCopyNodes(nodeIds)}
                    onPaste={
                      readOnly
                        ? () => {}
                        : () => void handlePasteFromClipboard()
                    }
                    onDuplicate={readOnly ? () => {} : handleDuplicateNodes}
                    onCreateVariableFromSelection={
                      readOnly ? undefined : handleCreateVariableFromSelection
                    }
                    onCreateFunctionFromSelection={
                      readOnly ? undefined : handleCreateFunctionFromSelection
                    }
                    onDelete={readOnly ? () => {} : handleDeleteNodes}
                  />
                  <PortConnectionPicker
                    menuState={
                      portConnectionMenu
                        ? {
                            mouseX: portConnectionMenu.mouseX,
                            mouseY: portConnectionMenu.mouseY,
                          }
                        : null
                    }
                    title={canvasDico.insertConnectedNode}
                    searchPlaceholder={canvasDico.searchNode}
                    emptyLabel={canvasDico.noCompatibleNode}
                    items={compatiblePortConnectionItems}
                    onClose={() => setPortConnectionMenu(null)}
                    onSelect={handleSelectPortConnectionTemplate}
                  />
                </div>
                {showSceneCatalogPanel && sceneCatalog && (
                  <SceneCatalogNodeList
                    catalog={sceneCatalog}
                    readOnly={readOnly}
                    visibleSections={catalogPanelSections}
                    excludeCustomEventId={editingCustomEventId}
                    editingObjectTypeId={editingObjectTypeId}
                  />
                )}
              </Box>
            </BehaviorFontContext.Provider>
          </FilterSvgPickerContext.Provider>
        </BehaviorPathSchemaProvider>
      </SceneObjectTypesProvider>
    );
  },
);

BehaviorCanvas.displayName = "BehaviorCanvas";
